import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faTimesCircle,
  faCalendarAlt,
  faTasks,
  faUser,
  faCheck,
  faSpinner,
  faInfo,
  faMusic,
  faBan,
  faUserPlus,
  faUserMinus,
  faEdit,
  faTrash,
  faCog,
  faBell,
  faEnvelope,
  faExclamationCircle,
  faThumbsUp,
  faUsers,
  faTimes,
  faMapMarkerAlt,
  faClock,
  faShieldAlt,
  faComment,
  faMicrophone,
  faUserShield,
  faExclamationTriangle,
  faMoneyBill,
  faCoins,
  faReply,
  faStar,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { 
  getNotifications, 
  markAsRead, 
  NOTIFICATION_TYPES,
  setupNotificationsListener,
  getBandNotifications,
  markBandNotificationAsRead,
  isBandNotificationRead,
  getUnreadBandNotificationsCount,
  setupBandNotificationsListener,
  processNotificationUIUpdates,
  acceptEventInvitation,
  rejectEventInvitation,
  acceptStaffRequest,
  rejectStaffRequest,
  createNotification,
  acceptBandEventInvitation,
  rejectBandEventInvitation,
  acceptManagerRequest,
  rejectManagerRequest,
  acceptArtistRequest,
  rejectArtistRequest,
  acceptVendorStaffRequest,
  rejectVendorStaffRequest,
  refreshNotifications, 
} from '../../utils/NotificationService';
import { updateUserEventDate } from '../../utils/TourSyncUtils';
import { handleMembershipRequest } from '../../utils/BandUtils'
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import styles from './Notifications.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import { doc, getDoc, updateDoc, arrayUnion, getDocs, collection, serverTimestamp, setDoc, limit, increment, deleteDoc, query, where, addDoc, orderBy } from 'firebase/firestore';
import { normalizeDate } from '../../utils/dateUtils';


const processedNotificationIds = new Set();


const Notifications = ({ onClose }) => {
const [activeTab, setActiveTab] = useState('general');
const [notifications, setNotifications] = useState([]);
const [bandNotifications, setBandNotifications] = useState([]);
const [loading, setLoading] = useState(true);
const [loadingMore, setLoadingMore] = useState(false);
const [hasMore, setHasMore] = useState(false);
const [lastChunk, setLastChunk] = useState(null);
const [lastBandItem, setLastBandItem] = useState(null);
const [unreadCount, setUnreadCount] = useState({ general: 0, invitations: 0, bands: 0 });
const [error, setError] = useState(null);
const allNotificationsRef = useRef({ general: [], invitations: [], bands: [] });
const pageSize = 15;
const currentPage = useRef(1);
const userBandsRef = useRef([]);
const [isProcessing, setIsProcessing] = useState(false);
const { userData, setUserData, updateBandMembership, refreshBandData } = useContext(UserContext);
const { updateEventData, fetchEventsData  } = useContext(TourContext);
const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
// Add state for notification details modal
const [selectedNotification, setSelectedNotification] = useState(null);
const [isModalOpen, setIsModalOpen] = useState(false);
const [showInvitationConflictModal, setShowInvitationConflictModal] = useState(false);
const [invitationConflictDetails, setInvitationConflictDetails] = useState(null);

const navigate = useNavigate();
useEffect(() => {
const handleResize = () => {
setIsMobile(window.innerWidth <= 768);
};
window.addEventListener('resize', handleResize);
return () => {
  window.removeEventListener('resize', handleResize);
};
}, []);

// New modal component for notification details
const NotificationDetailsModal = ({
  notification,
  onClose,
  handleEventInvitationAction,
  handleStaffRequest,
  handleVendorRequestAction,
  handleConnectionRequestAction,
  handleManagerRequest,
  handleArtistRequest
  }) => {
  if (!notification) return null;
  // Format permissions for display
  const formatPermissions = (permissions) => {
  if (!permissions) return [];
  return Object.entries(permissions)
    .filter(([key, value]) => value === true)
    .map(([key]) => {
      // Convert camelCase to readable format
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace('Can ', '');
    });
  };
  // Get notification-type specific content
  const getNotificationContent = () => {
  switch (notification.type) {
  case NOTIFICATION_TYPES.TOUR_INVITATION:
  case NOTIFICATION_TYPES.SHOW_INVITATION:
  case 'STAFF_REQUEST':
  case 'VENDOR_REQUEST':
  const permissions = formatPermissions(notification.permissions);
  const assignedDates = notification.assignedDates || [];
      return (
        <div className={styles.modalContent}>
          <h3>{notification.title}</h3>
          <p>{notification.message}</p>
          
          <div className={styles.detailSection}>
            <h4>Event Details</h4>
            <p><strong>Name:</strong> {notification.tourName || notification.data?._displayData?.tourName}</p>
            <p><strong>Act:</strong> {notification.actName || notification.data?._displayData?.actName}</p>
            {notification.createdBy && (
              <p><strong>Requested by:</strong> {notification.createdBy.fullName}</p>
            )}
          </div>
          
          {assignedDates.length > 0 && (
            <div className={styles.detailSection}>
              <h4>Assigned Dates</h4>
              <ul className={styles.datesList}>
                {assignedDates.map((date, index) => (
                  <li key={index} className={styles.dateItem}>
                    <FontAwesomeIcon icon={faCalendarAlt} className={styles.dateIcon} />
                    {date.date}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {permissions.length > 0 && (
            <div className={styles.detailSection}>
              <h4>Permissions</h4>
              <ul className={styles.permissionsList}>
                {permissions.map((permission, index) => (
                  <li key={index} className={styles.permissionItem}>
                    <FontAwesomeIcon icon={faShieldAlt} className={styles.permissionIcon} />
                    {permission}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {(!notification.status || notification.status === 'pending') && (
            <div className={styles.modalActions}>
              <button 
                className={styles.acceptButton}
                onClick={() => {
                  if (notification.type === NOTIFICATION_TYPES.TOUR_INVITATION || 
                      notification.type === NOTIFICATION_TYPES.SHOW_INVITATION) {
                    handleEventInvitationAction(notification, true);
                  } else if (notification.type === 'STAFF_REQUEST') {
                    handleStaffRequest(notification, true);
                  } else if (notification.type === 'VENDOR_REQUEST') {
                    handleVendorRequestAction(notification, true);
                  }
                  onClose();
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={() => {
                  if (notification.type === NOTIFICATION_TYPES.TOUR_INVITATION || 
                      notification.type === NOTIFICATION_TYPES.SHOW_INVITATION) {
                    handleEventInvitationAction(notification, false);
                  } else if (notification.type === 'STAFF_REQUEST') {
                    handleStaffRequest(notification, false);
                  } else if (notification.type === 'VENDOR_REQUEST') {
                    handleVendorRequestAction(notification, false);
                  }
                  onClose();
                }}
              >
                Decline
              </button>
            </div>
          )}
        </div>
      );

      

      case 'EARNING_RECORDED':
  return {
    icon: getNotificationIcon(notification),
    content: (
      <div className={styles.notificationContent}>
        <div className={styles.notificationHeader}>
          <h3>{notification.title}</h3>
          <span className={styles.timestamp}>
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
        <p>{notification.message}</p>
        {notification.data?._displayData?.eventName && (
          <span className={styles.eventName}>
            {notification.data._displayData.eventName}
          </span>
        )}
        {notification.data?._displayData?.venueName && (
          <span className={styles.venueName}>
            {notification.data._displayData.venueName}
          </span>
        )}
        <div className={styles.amountDisplay}>
          <FontAwesomeIcon icon={faCoins} className={styles.earningIcon} />
          <span className={styles.amount}>
            {notification.data?._displayData?.amount}
          </span>
        </div>
      </div>
    )
  };

case 'EXPENSE_OWED':
  return {
    icon: getNotificationIcon(notification),
    content: (
      <div className={styles.notificationContent}>
        <div className={styles.notificationHeader}>
          <h3>{notification.title}</h3>
          <span className={styles.timestamp}>
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
        <p>{notification.message}</p>
        {notification.data?._displayData?.eventName && (
          <span className={styles.eventName}>
            {notification.data._displayData.eventName}
          </span>
        )}
        {notification.data?._displayData?.venueName && (
          <span className={styles.venueName}>
            {notification.data._displayData.venueName}
          </span>
        )}
        <div className={styles.amountDisplay}>
          <FontAwesomeIcon icon={faMoneyBill} className={styles.expenseIcon} />
          <span className={styles.amount}>
            {notification.data?._displayData?.amount}
          </span>
        </div>
      </div>
    )
  };

case 'EXPENSE_SETTLED':
  return {
    icon: getNotificationIcon(notification),
    content: (
      <div className={styles.notificationContent}>
        <div className={styles.notificationHeader}>
          <h3>{notification.title}</h3>
          <span className={styles.timestamp}>
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
        <p>{notification.message}</p>
        {notification.data?._displayData?.eventName && (
          <span className={styles.eventName}>
            {notification.data._displayData.eventName}
          </span>
        )}
        {notification.data?._displayData?.venueName && (
          <span className={styles.venueName}>
            {notification.data._displayData.venueName}
          </span>
        )}
        <div className={styles.amountDisplay}>
          <FontAwesomeIcon icon={faCheck} className={styles.settledIcon} />
          <span className={styles.amount}>
            {notification.data?._displayData?.amount}
          </span>
        </div>
      </div>
    )
  };

      case NOTIFICATION_TYPES.ASSOCIATION_REQUEST:
        const requestData = notification.data?.requestData || {};
        const requesterName = requestData.requestedBy?.fullName || notification.data?._displayData?.fullName || "User";
        const requesterUserName = requestData.requestedBy?.userName || notification.data?._displayData?.userName || "";
        const requesterPhoto = requestData.requestedBy?.profilePictureUrl || notification.data?._displayData?.profilePictureUrl || defaultProfilePhoto;
        const requesterRole = requestData.role || notification.data?._displayData?.userRole || "Role not specified";
        const requesterId = requestData.requestedBy?.userId || notification.data?.senderUserId || "";
        const isPastRequest = requestData.isPast || false;
        
        return (
          <div className={styles.modalContent}>
            <h3>{notification.title}</h3>
            <p>{notification.message}</p>
            
            <div className={styles.detailSection}>
              <h4>Requester Details</h4>
              <div className={styles.userDetail}>
                <img 
                  src={requesterPhoto} 
                  alt={requesterName}
                  className={styles.userDetailPhoto}
                />
                <div>
                  <p><strong>Name:</strong> {requesterName}</p>
                  {requesterUserName && <p><strong>Username:</strong> @{requesterUserName}</p>}
                  <p><strong>Role:</strong> {requesterRole}</p>
                  {isPastRequest ? (
                    <p><strong>Type:</strong> Past Association</p>
                  ) : (
                    <p><strong>Type:</strong> Current Association</p>
                  )}
                  
                  {/* Show duration details for past association requests */}
                  {isPastRequest && requestData.duration && (
                    <p>
                      <strong>Duration:</strong> {formatDuration(requestData.duration)}
                    </p>
                  )}
                  
                  {/* Show since details for current association requests */}
                  {!isPastRequest && requestData.since && (
                    <p>
                      <strong>Since:</strong> {formatSince(requestData.since)}
                    </p>
                  )}
                </div>
              </div>
              
              {requesterId && (
                <div className={styles.viewProfileButton}>
                  <button 
                    className={styles.primaryButton}
                    onClick={() => {
                      navigateToUserProfile(requesterId);
                      onClose();
                    }}
                  >
                    View Profile
                  </button>
                </div>
              )}
            </div>
            
            {(!notification.status || notification.status === 'pending') && (
              <div className={styles.modalActions}>
                <button 
                  className={styles.acceptButton}
                  onClick={() => {
                    handleAssociationRequest(notification, true);
                    onClose();
                  }}
                >
                  Accept
                </button>
                <button 
                  className={styles.declineButton}
                  onClick={() => {
                    handleAssociationRequest(notification, false);
                    onClose();
                  }}
                >
                  Decline
                </button>
              </div>
            )}
            
            {notification.status === 'accepted' && (
              <div className={styles.statusMessage}>
                <span className={styles.acceptedStatus}>You accepted this request</span>
              </div>
            )}
            
            {notification.status === 'rejected' && (
              <div className={styles.statusMessage}>
                <span className={styles.rejectedStatus}>You declined this request</span>
              </div>
            )}
          </div>
        );
      
  
        case NOTIFICATION_TYPES.ORGMANAGER_REQUEST:
          case NOTIFICATION_TYPES.ORGARTIST_REQUEST:
            const roleType = notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST ? 'Organization Manager' : 'Managed Artist';
            // Access the organization name and type from the correct location in data._displayData
            const organizationName = notification.data?._displayData?.organizationName || notification.organizationName || "Organization";
            const organizationType = notification.data?._displayData?.organizationType || notification.organizationType || "";
            
            return (
              <div className={styles.modalContent}>
                <h3>{notification.title}</h3>
                <p>{notification.message}</p>
                
                <div className={styles.detailSection}>
                  <h4>Organization Details</h4>
                  <div className={styles.userDetail}>
                    <img 
                      src={notification.createdBy?.profilePictureUrl || defaultProfilePhoto} 
                      alt={organizationName}
                      className={styles.userDetailPhoto}
                    />
                    <div>
                      <p><strong>Name:</strong> {organizationName}</p>
                      <p><strong>Type:</strong> {organizationType}</p>
                      {notification.createdBy?.userName && (
                        <p><strong>Username:</strong> @{notification.createdBy.userName}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.viewProfileButton}>
                    <button 
                      className={styles.primaryButton}
                      onClick={() => {
                        if (notification.createdBy?.userName) {
                          navigate(`/${notification.createdBy.userName}`);
                          onClose();
                        } else if (notification.organizationId) {
                          navigateToUserProfile(notification.organizationId);
                          onClose();
                        }
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
                
                {/* Add accept/reject buttons for pending requests */}
                {(!notification.status || notification.status === 'pending') && (
                  <div className={styles.modalActions}>
                    <button 
                      className={styles.acceptButton}
                      onClick={() => {
                        if (notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST) {
                          handleManagerRequest(notification, true);
                        } else {
                          handleArtistRequest(notification, true);
                        }
                        onClose();
                      }}
                    >
                      Accept
                    </button>
                    <button 
                      className={styles.declineButton}
                      onClick={() => {
                        if (notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST) {
                          handleManagerRequest(notification, false);
                        } else {
                          handleArtistRequest(notification, false);
                        }
                        onClose();
                      }}
                    >
                      Decline
                    </button>
                  </div>
                )}
                
                {/* Show status message when request has been processed */}
                {notification.status === 'accepted' && (
                  <div className={styles.statusMessage}>
                    <span className={styles.acceptedStatus}>
                      You accepted this {notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST ? 'manager' : 'artist'} request
                    </span>
                  </div>
                )}
                
                {notification.status === 'rejected' && (
                  <div className={styles.statusMessage}>
                    <span className={styles.rejectedStatus}>
                      You declined this {notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST ? 'manager' : 'artist'} request
                    </span>
                  </div>
                )}
              </div>
            );

    case NOTIFICATION_TYPES.TASK_ASSIGNED:
      return (
        <div className={styles.modalContent}>
          <h3>{notification.title}</h3>
          <p>{notification.message}</p>
          
          <div className={styles.detailSection}>
            <h4>Task Details</h4>
            <p><strong>Event:</strong> {notification.data?._displayData?.tourName}</p>
            <p><strong>Assigned by:</strong> {notification.data?.actionBy?.fullName || 'Unknown'}</p>
            <p><strong>Due date:</strong> {notification.data?.dueDate || 'Not specified'}</p>
          </div>
        </div>
      );
      
    case 'CONNECTION_REQUEST':
      return (
        <div className={styles.modalContent}>
          <h3>{notification.title}</h3>
          <p>{notification.message}</p>
          
          <div className={styles.detailSection}>
            <h4>User Details</h4>
            {notification.data?._displayData && (
              <div className={styles.userDetail}>
                <img 
                  src={notification.data._displayData.profilePictureUrl || defaultProfilePhoto} 
                  alt={notification.data._displayData.fullName || "User"}
                  className={styles.userDetailPhoto}
                />
                <div>
                  <p><strong>Name:</strong> {notification.data._displayData.fullName}</p>
                  <p><strong>Username:</strong> {notification.data._displayData.userName}</p>
                </div>
              </div>
            )}
          </div>
          
          {(!notification.status || notification.status === 'pending') && (
            <div className={styles.modalActions}>
              <button 
                className={styles.acceptButton}
                onClick={() => {
                  handleConnectionRequestAction(notification, true);
                  onClose();
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={() => {
                  handleConnectionRequestAction(notification, false);
                  onClose();
                }}
              >
                Decline
              </button>
            </div>
          )}
        </div>
      );

      case 'NEW_FEEDBACK':
case 'FEEDBACK_UPDATED':
case 'FEEDBACK_REPLY':
  return {
    icon: getNotificationIcon(notification),
    content: (
      <div className={styles.notificationContent}>
        <div className={styles.notificationHeader}>
          <h3>{notification.title}</h3>
          <span className={styles.timestamp}>
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
        <p>{notification.message}</p>
        {notification.data?._displayData?.fullName && (
          <div className={styles.actionBy}>
            <img 
              src={notification.data?._displayData?.profilePictureUrl || defaultProfilePhoto} 
              alt={notification.data?._displayData?.fullName}
              className={styles.actionByPhoto}
            />
            <span>{notification.data?._displayData?.fullName}</span>
          </div>
        )}
      </div>
    )
  };
  

case 'NEW_REVIEW':
case 'REVIEW_UPDATED':
case 'REVIEW_REPLY':
  return {
    icon: getNotificationIcon(notification),
    content: (
      <div className={styles.notificationContent}>
        <div className={styles.notificationHeader}>
          <h3>{notification.title}</h3>
          <span className={styles.timestamp}>
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
        <p>{notification.message}</p>
        {notification.data?.rating && (
          <div className={styles.reviewRating}>
            <span>{notification.data.rating} ★</span>
          </div>
        )}
        {notification.data?._displayData?.fullName && (
          <div className={styles.actionBy}>
            <img 
              src={notification.data?._displayData?.profilePictureUrl || defaultProfilePhoto} 
              alt={notification.data?._displayData?.fullName}
              className={styles.actionByPhoto}
            />
            <span>{notification.data?._displayData?.fullName}</span>
          </div>
        )}
      </div>
    )
  };

  case NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST:
    case NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST:
      const isPastStaff = notification.type === NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST;
      const vendorName = notification.organizationName || notification.data?._displayData?.vendorName;
      const vendorType = notification.organizationType || notification.data?._displayData?.vendorType;
      const vendorId = notification.organizationId;
      const formattedPermissions = formatPermissions(notification.permissions);
      
      return (
        <div className={styles.modalContent}>
          <h3>{notification.title}</h3>
          <p>{notification.message}</p>
          
          <div className={styles.detailSection}>
            <h4>Vendor Details</h4>
            <p><strong>Name:</strong> {vendorName}</p>
            <p><strong>Type:</strong> {vendorType}</p>
            {notification.createdBy && (
              <p><strong>Requested by:</strong> {notification.createdBy.fullName}</p>
            )}
            
            {/* Add vendor profile button */}
            {vendorId && (
              <div className={styles.viewProfileButton}>
                <button 
                  className={styles.primaryButton}
                  onClick={() => {
                    navigateToUserProfile(vendorId);
                    onClose();
                  }}
                >
                  View Vendor Profile
                </button>
              </div>
            )}
          </div>
          
          {formattedPermissions.length > 0 && (
            <div className={styles.detailSection}>
              <h4>Permissions</h4>
              <ul className={styles.permissionsList}>
                {formattedPermissions.map((permission, index) => (
                  <li key={index} className={styles.permissionItem}>
                    <FontAwesomeIcon icon={faShieldAlt} className={styles.permissionIcon} />
                    {permission}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {isPastStaff ? (
            <div className={styles.detailSection}>
              <h4>Duration</h4>
              <p>
                <strong>From:</strong> {notification.data?._displayData?.startMonth}/{notification.data?._displayData?.startYear}
              </p>
              <p>
                <strong>To:</strong> {notification.data?._displayData?.endMonth}/{notification.data?._displayData?.endYear}
              </p>
            </div>
          ) : (
            <div className={styles.detailSection}>
              <h4>Since</h4>
              <p>
                <strong>Since:</strong> {notification.data?._displayData?.sinceMonth}/{notification.data?._displayData?.sinceYear}
              </p>
            </div>
          )}
          
          {(!notification.status || notification.status === 'pending') && (
            <div className={styles.modalActions}>
              <button 
                className={styles.acceptButton}
                onClick={() => {
                  handleVendorStaffRequest(notification, true);
                  onClose();
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={() => {
                  handleVendorStaffRequest(notification, false);
                  onClose();
                }}
              >
                Decline
              </button>
            </div>
          )}
        </div>
      );
      
    case NOTIFICATION_TYPES.VENDOR_STAFF_PERMISSIONS_UPDATED:
      const updatedPermissions = formatPermissions(notification.data?.permissions);
      
      return (
        <div className={styles.modalContent}>
          <h3>{notification.title}</h3>
          <p>{notification.message}</p>
          
          <div className={styles.detailSection}>
            <h4>Vendor Details</h4>
            <p><strong>Name:</strong> {notification.data?._displayData?.vendorName}</p>
            <p><strong>Type:</strong> {notification.data?._displayData?.vendorType}</p>
          </div>
          
          <div className={styles.detailSection}>
            <h4>Updated Permissions</h4>
            {updatedPermissions.length > 0 ? (
              <ul className={styles.permissionsList}>
                {updatedPermissions.map((permission, index) => (
                  <li key={index} className={styles.permissionItem}>
                    <FontAwesomeIcon icon={faShieldAlt} className={styles.permissionIcon} />
                    {permission}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No special permissions granted</p>
            )}
          </div>
        </div>
      );
      
      case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REQUEST:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST:
  const isPastVendorRequest = notification.type === NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST;
  const staffName = notification.data?.requestData?.requestedBy?.fullName || notification.data?._displayData?.fullName || "Staff";
  const staffUserName = notification.data?.requestData?.requestedBy?.userName || notification.data?._displayData?.userName || "";
  const staffPhoto = notification.data?.requestData?.requestedBy?.profilePictureUrl || notification.data?._displayData?.profilePictureUrl || defaultProfilePhoto;
  const staffRole = notification.data?.requestData?.role || notification.data?._displayData?.userRole || "staff";
  const staffId = notification.data?.requestData?.requestedBy?.userId || notification.data?.userId || "";
  
  return (
    <div className={styles.modalContent}>
      <h3>{notification.title}</h3>
      <p>{notification.message}</p>
      
      <div className={styles.detailSection}>
        <h4>Requester Details</h4>
        <div className={styles.userDetail}>
          <img 
            src={staffPhoto} 
            alt={staffName}
            className={styles.userDetailPhoto}
          />
          <div>
            <p><strong>Name:</strong> {staffName}</p>
            {staffUserName && <p><strong>Username:</strong> @{staffUserName}</p>}
            <p><strong>Role:</strong> {staffRole}</p>
            {isPastVendorRequest ? (
              <p><strong>Type:</strong> Past Association</p>
            ) : (
              <p><strong>Type:</strong> Current Association</p>
            )}
            
            {/* Show duration details for past association requests */}
            {isPastVendorRequest && notification.data?.requestData?.duration && (
              <p>
                <strong>Duration:</strong> {formatDuration(notification.data.requestData.duration)}
              </p>
            )}
            
            {/* Show since details for current association requests */}
            {!isPastVendorRequest && notification.data?.requestData?.since && (
              <p>
                <strong>Since:</strong> {formatSince(notification.data.requestData.since)}
              </p>
            )}
          </div>
        </div>
        
        {staffId && (
          <div className={styles.viewProfileButton}>
            <button 
              className={styles.primaryButton}
              onClick={() => {
                navigateToUserProfile(staffId);
                onClose();
              }}
            >
              View Profile
            </button>
          </div>
        )}
      </div>
      
      {(!notification.status || notification.status === 'pending') && (
        <div className={styles.modalActions}>
          <button 
            className={styles.acceptButton}
            onClick={() => {
              handleAssociatedVendorRequest(notification, true);
              onClose();
            }}
          >
            Accept
          </button>
          <button 
            className={styles.declineButton}
            onClick={() => {
              handleAssociatedVendorRequest(notification, false);
              onClose();
            }}
          >
            Decline
          </button>
        </div>
      )}
      
      {notification.status === 'accepted' && (
        <div className={styles.statusMessage}>
          <span className={styles.acceptedStatus}>You accepted this request</span>
        </div>
      )}
      
      {notification.status === 'rejected' && (
        <div className={styles.statusMessage}>
          <span className={styles.rejectedStatus}>You declined this request</span>
        </div>
      )}
    </div>
  );


    default:
      return (
        <div className={styles.modalContent}>
          <h3>{notification.title}</h3>
          <p>{notification.message}</p>
        </div>
      );
  }
  };
  return (
  <div className={styles.modalOverlay} onClick={onClose}>
  <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
  <button className={styles.modalCloseButton} onClick={onClose}>
  <FontAwesomeIcon icon={faTimes} />
  </button>
      {getNotificationContent()}
    </div>
  </div>
  );
  };


// Function to handle new notifications
const handleNewNotification = useCallback((notificationData) => {
  // Process notification UI updates immediately if relevant
  if (notificationData.type === 'band' && notificationData.newNotifications?.length > 0) {
    notificationData.newNotifications.forEach(notification => {
      if (userData && updateBandMembership && refreshBandData && setUserData) {
        // Process UI updates based on notification type
        processNotificationUIUpdates(
          notification,
          userData,
          { updateBandMembership, refreshBandData, setUserData }
        );
      }
    });
  }

  // Filter out already processed notifications
  let newItems = [];
  if (notificationData.type === 'band') {
    newItems = notificationData.newNotifications.filter(item => !processedNotificationIds.has(item.id));
    // Add newly processed IDs to the set
    newItems.forEach(item => processedNotificationIds.add(item.id));
  } else {
    const items = notificationData.type === 'general' 
      ? notificationData.newNotifications 
      : notificationData.newInvitations;
    newItems = items.filter(item => !processedNotificationIds.has(item.id));
    // Add newly processed IDs to the set
    newItems.forEach(item => processedNotificationIds.add(item.id));
  }

  // Only update count if we have truly new items
  if (newItems.length > 0) {
    // Update unread count
    setUnreadCount(prev => {
      if (notificationData.type === 'band') {
        return {
          ...prev,
          bands: prev.bands + newItems.length
        };
      } else {
        return {
          ...prev,
          [notificationData.type]: prev[notificationData.type] + newItems.length
        };
      }
    });
  }

  // Update displayed notifications if on the relevant tab
  if (activeTab === notificationData.type) {
    const newItems = notificationData.type === 'general' 
      ? notificationData.newNotifications 
      : notificationData.type === 'invitations'
        ? notificationData.newInvitations
        : notificationData.newNotifications;
    
    // Add metadata to identify new notifications for animations
    const itemsWithMetadata = newItems.map(item => ({
      ...item,
      isNew: true, // Used for animation
      receivedAt: new Date() // Timestamp when received
    }));
    
    if (notificationData.type === 'band') {
      // For band notifications, add band info
      const itemsWithBandInfo = itemsWithMetadata.map(item => ({
        ...item,
        bandInfo: {
          id: notificationData.bandId,
          name: bandNameMap.current[notificationData.bandId] || "Unknown Band",
          username: bandUsernameMap.current[notificationData.bandId] || notificationData.bandId
        }
      }));
      
      // IMPORTANT: Use functional update to deduplicate band notifications
      setBandNotifications(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const uniqueNewItems = itemsWithBandInfo.filter(item => !existingIds.has(item.id));
        
        if (uniqueNewItems.length > 0) {
          // Also update the all notifications ref
          allNotificationsRef.current.bands = [...uniqueNewItems, ...allNotificationsRef.current.bands];
          return [...uniqueNewItems, ...prev];
        }
        return prev;
      });
    } else {
      // IMPORTANT: Use functional update to deduplicate general/invitation notifications
      setNotifications(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const uniqueNewItems = itemsWithMetadata.filter(item => !existingIds.has(item.id));
        
        if (uniqueNewItems.length > 0) {
          // Also update the all notifications ref
          allNotificationsRef.current[activeTab] = [...uniqueNewItems, ...allNotificationsRef.current[activeTab]];
          return [...uniqueNewItems, ...prev];
        }
        return prev;
      });
    }
  }
}, [activeTab, userData, updateBandMembership, refreshBandData, setUserData]); 


// Add a reference to map band IDs to their names and usernames
const bandNameMap = useRef({});
const bandUsernameMap = useRef({});
// Extract user bands on component mount
useEffect(() => {
if (userData?.associatedActs) {
// Get bands from associated acts
const bands = userData.associatedActs.filter(act => act.type === 'band');
userBandsRef.current = bands;
  // Also update the name and username maps at the same time
  const nameMap = {};
  const usernameMap = {};
  
  bands.forEach(band => {
    nameMap[band.id] = band.name;
    usernameMap[band.id] = band.username;
  });
  
  bandNameMap.current = nameMap;
  bandUsernameMap.current = usernameMap;
}
}, [userData]);
// Set up real-time listener
useEffect(() => {
if (!userData?.userId) return;
// Set up listener for user notifications
const unsubscribeUser = setupNotificationsListener(userData.userId, handleNewNotification);

// Set up listeners for each band
const bandUnsubscribes = [];

if (userBandsRef.current.length > 0) {
  userBandsRef.current.forEach(band => {
    const unsubscribe = setupBandNotificationsListener(
      band.id, 
      userData.userId, 
      handleNewNotification
    );
    bandUnsubscribes.push(unsubscribe);
  });
}

// Cleanup
return () => {
  unsubscribeUser();
  bandUnsubscribes.forEach(unsubscribe => unsubscribe());
};
}, [userData?.userId, handleNewNotification]);


// Format duration for display
const formatDuration = (duration) => {
  if (!duration) return '';
  
  const { startMonth, startYear, endMonth, endYear } = duration;
  
  // Get month names
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const startMonthName = months[parseInt(startMonth) - 1] || '';
  const endMonthName = months[parseInt(endMonth) - 1] || '';
  
  return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
};

// Format "since" date for display
const formatSince = (since) => {
  if (!since) return '';
  
  const { month, year } = since;
  
  // Get month names
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const monthName = months[parseInt(month) - 1] || '';
  
  return `${monthName} ${year}`;
};



// Complete updated fetchNotifications function
const fetchNotifications = useCallback(async (forceFetch = false) => {
  if (!userData?.userId) return;
  try {
    setLoading(true);
    setError(null);
    currentPage.current = 1;
    
    // Reset processed notification tracking when doing a fresh fetch
    if (forceFetch) {
      processedNotificationIds.clear();
    }
  currentPage.current = 1;
  if (activeTab === 'bands') {
      // Fetch band notifications
      if (userBandsRef.current.length === 0) {
        setBandNotifications([]);
        setHasMore(false);
        setLastBandItem(null);
        allNotificationsRef.current.bands = [];
        setUnreadCount(prev => ({
          ...prev,
          bands: 0
        }));
        setLoading(false);
        return;
      }
  
      // Create a map to track duplicate notifications (same ID from different band fetches)
      const notificationMap = new Map();
      let totalUnreadCount = 0;
  
      await Promise.all(userBandsRef.current.map(async band => {
        try {
          const result = await getBandNotifications(band.id, null, pageSize * 2, forceFetch);
          
          // Add band info to each notification and track in map
          result.items.forEach(item => {
            // Check if we already have this notification (could happen with band admins in multiple bands)
            if (!notificationMap.has(item.id)) {
              notificationMap.set(item.id, {
                ...item,
                bandInfo: {
                  id: band.id,
                  name: band.name,
                  username: band.username,
                  photoUrl: band.photoUrl
                }
              });
              
              // Count unread
              if (!isBandNotificationRead(item, userData.userId)) {
                totalUnreadCount++;
              }
            }
          });
        } catch (error) {
          console.error(`Error fetching notifications for band ${band.id}:`, error);
        }
      }));
  
      // Convert map to array and sort by timestamp
      const allBandNotifications = Array.from(notificationMap.values()).sort((a, b) => {
        // Handle different timestamp formats (Firestore Timestamp or regular Date)
        const timeA = a.timestamp?.seconds ? a.timestamp.seconds : 
                     (a.timestamp?.toDate ? a.timestamp.toDate().getTime() / 1000 : 0);
        const timeB = b.timestamp?.seconds ? b.timestamp.seconds : 
                     (b.timestamp?.toDate ? b.timestamp.toDate().getTime() / 1000 : 0);
        return timeB - timeA; // Newest first
      });
  
      // Update state with first page of notifications
      setBandNotifications(allBandNotifications.slice(0, pageSize));
      setHasMore(allBandNotifications.length > pageSize);
      setLastBandItem(allBandNotifications.length > pageSize ? 
        allBandNotifications[pageSize - 1] : null);
      
      // Store all for pagination
      allNotificationsRef.current.bands = allBandNotifications;
      
      // Update unread count
      setUnreadCount(prev => ({
        ...prev,
        bands: totalUnreadCount
      }));
      
      // Update band name and username maps for future real-time notifications
      const nameMap = {};
      const usernameMap = {};
      
      userBandsRef.current.forEach(band => {
        nameMap[band.id] = band.name;
        usernameMap[band.id] = band.username;
      });
      
      bandNameMap.current = nameMap;
      bandUsernameMap.current = usernameMap;
  } else {
    // Fetch general or invitation notifications using new individual document approach
    const result = await getNotifications(
      userData.userId, 
      activeTab, 
      null, 
      8, // Changed from 15 to 8
      forceFetch
    );
    
    // Deduplicate notifications based on ID
    const uniqueNotifications = [];
    const seenIds = new Set();
    
    for (const item of result.items) {
      if (!seenIds.has(item.id)) {
        seenIds.add(item.id);
        uniqueNotifications.push(item);
      }
    }
    
    setNotifications(uniqueNotifications);
    setHasMore(result.hasMore);
    setLastChunk(result.lastDoc); // This is now the last document instead of chunk
  
    // Update unread count
    const unread = uniqueNotifications.filter(item => !item.read).length;
    setUnreadCount(prev => ({
      ...prev,
      [activeTab]: unread
    }));
    
    // Store unique notifications in all notifications cache
    allNotificationsRef.current[activeTab] = uniqueNotifications;
  }
  } catch (error) {
  console.error('Error loading notifications:', error);
  setError('Failed to load notifications. Please try again.');
  if (activeTab === 'bands') {
  setBandNotifications([]);
  } else {
  setNotifications([]);
  }
  setHasMore(false);
 } finally {
    setLoading(false);
  }
}, [userData?.userId, activeTab]);

  
// Initial fetch when tab changes
useEffect(() => {
fetchNotifications();
}, [activeTab, fetchNotifications]);


const handleLoadMore = useCallback(() => {
if (loadingMore || !hasMore) return;
try {
  setLoadingMore(true);
  currentPage.current += 1;
  
  if (activeTab === 'bands') {
    // Load more band notifications from cache
    const nextItems = allNotificationsRef.current.bands.slice(
      (currentPage.current - 1) * pageSize, 
      currentPage.current * pageSize
    );
    
    if (nextItems.length > 0) {
      setBandNotifications(prev => [...prev, ...nextItems]);
    }
    
    // Check if there are more items
    setHasMore(currentPage.current * pageSize < allNotificationsRef.current.bands.length);
  } else {
    // Get the next page from cached notifications for general/invitations
    const nextItems = allNotificationsRef.current[activeTab].slice(
      (currentPage.current - 1) * pageSize, 
      currentPage.current * pageSize
    );
    
    if (nextItems.length > 0) {
      setNotifications(prev => [...prev, ...nextItems]);
    }
    
    // Check if there are more items
    setHasMore(currentPage.current * pageSize < allNotificationsRef.current[activeTab].length);
  }
} catch (error) {
  console.error('Error loading more notifications:', error);
  setError('Failed to load more notifications. Please try again.');
} finally {
  setLoadingMore(false);
}
}, [activeTab, loadingMore, hasMore]);


const handleAssociationRequest = async (notification, isAccepted) => {
  try {
    setIsProcessing(true);
    const db = getFirestoreInstance();
    
    const requestData = notification.data?.requestData;
    if (!requestData) {
      throw new Error('Request data is missing');
    }
    
    const requesterId = requestData.requestedBy?.userId;
    const requesterUserType = requestData.requestedBy?.userType;
    const isPastRequest = requestData.isPast || false;
    
    if (isAccepted) {
      // Accept the association request
      
      // 1. Add to requester's associatedActs subcollection
      const associatedActsRef = collection(db, 'users', requesterId, 'associatedActs');
      await addDoc(associatedActsRef, {
        id: userData.userId,
        type: 'artist', // Or use userData.userType to determine
        name: userData.fullName,
        username: userData.userName,
        photoUrl: userData.profilePictureUrl || '',
        subType: userData.userSubType || '',
        genre: userData.genres?.join(', ') || '',
        verified: userData.idVerificationStatus === 'Verified',
        role: requestData.role,
        isPast: isPastRequest,
        // Add appropriate date data based on whether this is past or current
        ...(isPastRequest ? 
          { duration: requestData.duration } : 
          { since: requestData.since }
        ),
        createdAt: new Date().toISOString(),
        associationId: requestData.associationId
      });
      
      // 2. If requester is a Manager, add to current user's managedBy array
      if (!isPastRequest && requesterUserType === 'Manager') {
        const userRef = doc(db, 'users', userData.userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const managedBy = userDoc.data().managedBy || [];
          
          // Only add if not already in the array
          if (!managedBy.some(manager => manager.userId === requesterId)) {
            await updateDoc(userRef, {
              managedBy: arrayUnion({
                userId: requesterId,
                userName: requestData.requestedBy.userName,
                fullName: requestData.requestedBy.fullName,
                profilePictureUrl: requestData.requestedBy.profilePictureUrl || '',
                since: requestData.since,
                role: requestData.role,
                addedAt: new Date().toISOString()
              })
            });
          }
        }
      }
      
      // 3. Update top 3 associations in the requester's main document
      const requesterRef = doc(db, 'users', requesterId);
      const collectionField = isPastRequest ? 'pastAssociatedActs' : 'associatedActs';
      const countField = isPastRequest ? 'pastAssociatedActsCount' : 'associatedActsCount';
      
      const actsQuery = query(
        associatedActsRef,
        where('isPast', '==', isPastRequest),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const actsSnapshot = await getDocs(actsQuery);
      const top3Acts = actsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Make sure all required fields are present in each act
      const validatedTop3Acts = top3Acts.map(act => {
        // Ensure these fields exist on each act
        return {
          id: act.id,
          type: act.type || 'artist',
          name: act.name || '',
          username: act.username || '',
          photoUrl: act.photoUrl || '',
          subType: act.subType || '',
          genre: act.genre || '',
          verified: act.verified || false,
          role: act.role || '',
          isPast: isPastRequest,
          ...(isPastRequest ? 
            { duration: act.duration || { startMonth: '', startYear: '', endMonth: '', endYear: '' } } : 
            { since: act.since || { month: '', year: '' } }
          ),
          createdAt: act.createdAt || new Date().toISOString(),
          associationId: act.associationId || `${act.id}-${act.role || ''}-${Date.now()}`
        };
      });
      
      // Update main document with validated top 3
      await updateDoc(requesterRef, {
        [collectionField]: validatedTop3Acts,
        [countField]: increment(1) // Increment the counter
      });
      
      // 4. Delete the pending request from requester's pendingAssociationRequests
      if (requestData.requestDocId) {
        try {
          const pendingRequestRef = doc(db, 'users', requesterId, 'pendingAssociationRequests', requestData.requestDocId);
          await deleteDoc(pendingRequestRef);
        } catch (error) {
          console.error('Error deleting pending request:', error);
          // Continue even if this fails
        }
      }
      
      // 5. Send acceptance notification to requester
      await createNotification(requesterId, 'general', {
        type: NOTIFICATION_TYPES.ASSOCIATION_ACCEPTED,
        title: isPastRequest ? 'Past Association Request Accepted' : 'Association Request Accepted',
        message: `${userData.fullName} has accepted your request to add them as a ${requestData.role} in your ${isPastRequest ? 'past' : 'current'} associated acts`,
        data: {
          userId: userData.userId,
          role: requestData.role,
          isPast: isPastRequest,
          _displayData: {
            userName: userData.userName,
            fullName: userData.fullName
          }
        }
      });
      
      // 6. Mark the notification as accepted
      await markNotificationAsProcessed(userData.userId, notification.id, 'accepted');
      
      // Update UI
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'accepted', read: true } 
            : n
        )
      );
      
    } else {
      // Reject the association request
      
      // 1. Delete the pending request from requester's pendingAssociationRequests
      if (requestData.requestDocId) {
        try {
          const pendingRequestRef = doc(db, 'users', requesterId, 'pendingAssociationRequests', requestData.requestDocId);
          await deleteDoc(pendingRequestRef);
        } catch (error) {
          console.error('Error deleting pending request:', error);
          // Continue even if this fails
        }
      }
      
      // 2. Send rejection notification to requester
      await createNotification(requesterId, 'general', {
        type: NOTIFICATION_TYPES.ASSOCIATION_REJECTED,
        title: isPastRequest ? 'Past Association Request Declined' : 'Association Request Declined',
        message: `${userData.fullName} has declined your request to add them as a ${requestData.role} in your ${isPastRequest ? 'past' : 'current'} associated acts`,
        data: {
          userId: userData.userId,
          role: requestData.role,
          isPast: isPastRequest,
          _displayData: {
            userName: userData.userName,
            fullName: userData.fullName
          }
        }
      });
      
      // 3. Mark the notification as rejected
      await markNotificationAsProcessed(userData.userId, notification.id, 'rejected');
      
      // Update UI
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'rejected', read: true } 
            : n
        )
      );
    }
    
    // Refresh notifications
    fetchNotifications(true);
    
  } catch (error) {
    console.error('Error handling association request:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} request. Please try again.`);
  } finally {
    setIsProcessing(false);
  }
};


/**

Navigate to a user profile by user ID
@param {string} userId - The user ID
@returns {Promise<boolean>} Success status
*/
const navigateToUserProfile = async (userId) => {
try {
const db = getFirestoreInstance();
const userDoc = await getDoc(doc(db, 'users', userId));
if (userDoc.exists()) {
const userData = userDoc.data();
if (userData.userName) {
  navigate(`/${userData.userName}`);
onClose();
return true;
} else {
console.error('User found but username missing:', userId);
alert('Could not navigate to this user profile.');
return false;
}
} else {
console.error('User not found:', userId);
alert('Could not find this user profile.');
return false;
}
} catch (error) {
console.error('Error navigating to user profile:', error);
alert('Error loading user profile.');
return false;
}
};

const handleConnectionRequestAction = async (notification, isAccepted) => {
try {
setIsProcessing(true);
  const db = getFirestoreInstance();
  const senderUserId = notification.data?.senderUserId;
  
  if (!senderUserId) {
    throw new Error('Sender information missing');
  }
  
  if (isAccepted) {
    // 1. Get both users' data for accurate information
    const senderUserDoc = await getDoc(doc(db, 'users', senderUserId));
    const currentUserDoc = await getDoc(doc(db, 'users', userData.userId));
    
    if (!senderUserDoc.exists() || !currentUserDoc.exists()) {
      throw new Error('User data not found');
    }
    
    const senderUserData = senderUserDoc.data();
    const currentUserData = currentUserDoc.data();
    
    // 2. Add connection to current user's connections as individual document
    const currentUserConnectionRef = doc(db, 'users', userData.userId, 'connections', senderUserId);
    await setDoc(currentUserConnectionRef, {
      userId: senderUserId,
      fullName: senderUserData.fullName || '',
      userName: senderUserData.userName || '',
      profilePictureUrl: senderUserData.profilePictureUrl || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // 3. Add connection to sender's connections as individual document
    const senderConnectionRef = doc(db, 'users', senderUserId, 'connections', userData.userId);
    await setDoc(senderConnectionRef, {
      userId: userData.userId,
      fullName: currentUserData.fullName || '',
      userName: currentUserData.userName || '',
      profilePictureUrl: currentUserData.profilePictureUrl || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // 4. Increment connections count for both users
    await updateDoc(doc(db, 'users', userData.userId), {
      connectionsCount: increment(1)
    });
    
    await updateDoc(doc(db, 'users', senderUserId), {
      connectionsCount: increment(1)
    });
    
    // 5. Remove the connection request
    await deleteDoc(doc(db, 'users', userData.userId, 'connectionRequests', senderUserId));
    
    // 6. Send notification to the sender that request was accepted
    const acceptanceNotificationData = {
      type: 'CONNECTION_ACCEPTED',
      title: 'Connection Accepted',
      message: `${currentUserData.fullName || currentUserData.userName} accepted your connection request.`,
      data: {
        userId: userData.userId,
        _displayData: {
          userName: currentUserData.userName,
          fullName: currentUserData.fullName,
          profilePictureUrl: currentUserData.profilePictureUrl || '',
        }
      }
    };
    
    await createNotification(senderUserId, 'general', acceptanceNotificationData);
    
    // 7. Update current notification status
    setNotifications(prev => 
      prev.map(n => 
        n.id === notification.id 
          ? { ...n, status: 'accepted', read: true } 
          : n
      )
    );
    
    // Also update the notifications cache
    allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
      n.id === notification.id 
        ? { ...n, status: 'accepted', read: true } 
        : n
    );
    
    await markNotificationAsProcessed(userData.userId, notification.id, 'accepted');
    
    alert('Connection request accepted!');
  } else {
    // Handle rejection
    // 1. Remove the connection request
    await deleteDoc(doc(db, 'users', userData.userId, 'connectionRequests', senderUserId));
    
    // 2. Update notification status
    setNotifications(prev => 
      prev.map(n => 
        n.id === notification.id 
          ? { ...n, status: 'rejected', read: true } 
          : n
      )
    );
    
    // Also update the notifications cache
    allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
      n.id === notification.id 
        ? { ...n, status: 'rejected', read: true } 
        : n
    );
    
    await markNotificationAsProcessed(userData.userId, notification.id, 'rejected');
    
    // 3. Send rejection notification (optional)
    const rejectionNotificationData = {
      type: 'CONNECTION_REJECTED',
      title: 'Connection Request Declined',
      message: `${userData.fullName || userData.userName} declined your connection request.`,
      data: {
        userId: userData.userId
      }
    };
    
    await createNotification(senderUserId, 'general', rejectionNotificationData);
    
    alert('Connection request declined.');
  }
  
  // Refresh notifications
  fetchNotifications(true);
} catch (error) {
  console.error('Error handling connection request:', error);
  alert(`Failed to ${isAccepted ? 'accept' : 'decline'} request. ${error.message}`);
} finally {
  setIsProcessing(false);
}
};
// Helper function to mark notification as processed
const markNotificationAsProcessed = async (userId, notificationId, status) => {
const db = getFirestoreInstance();
try {
  // Update the notification document with new status
  const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
  
  await updateDoc(notificationRef, {
    status: status,
    respondedAt: serverTimestamp(),
    read: true
  });
  
  return true;
} catch (error) {
  console.error('Error marking notification as processed:', error);
  throw error;
}
};

// Add this helper function
const handleVendorStaffRequest = (notification, isAccepted) => {
  handleStaffRequest(notification, isAccepted);
};

// Updated handleStaffRequest function for Notifications.js
const handleStaffRequest = async (notification, isAccepted) => {
  try {
    setIsProcessing(true);

    if (notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST || 
      notification.type === NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST) {
    try {
      if (isAccepted) {
        await acceptVendorStaffRequest(notification.id, userData.userId);
      } else {
        await rejectVendorStaffRequest(notification.id, userData.userId);
      }
      
      // Update UI state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: isAccepted ? 'accepted' : 'rejected', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: isAccepted ? 'accepted' : 'rejected', read: true } 
          : n
      );
      
      // Refresh notifications
      await refreshNotifications(userData.userId, true);
      
      // If the user data context has a refresh method, call it to update profile data
      if (userData && refreshBandData) {
        await refreshBandData(); // This will refresh band data which may be needed
      }

      // Force a refresh of the user data to update profile sections
      if (userData && setUserData) {
        // Get fresh user data
        const db = getFirestoreInstance();
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        if (userDoc.exists()) {
          const freshUserData = userDoc.data();
          // Update user context with fresh data
          setUserData({
            ...userData,
            ...freshUserData,
            // Ensure we're updating the specific fields that StaffSection and AssociatedVendorsSection use
            vendorStaff: freshUserData.vendorStaff || [],
            pastVendorStaff: freshUserData.pastVendorStaff || [],
            vendorStaffCount: freshUserData.vendorStaffCount || 0,
            pastVendorStaffCount: freshUserData.pastVendorStaffCount || 0,
            associatedVendors: freshUserData.associatedVendors || [],
            pastAssociatedVendors: freshUserData.pastAssociatedVendors || [],
            associatedVendorsCount: freshUserData.associatedVendorsCount || 0,
            pastAssociatedVendorsCount: freshUserData.pastAssociatedVendorsCount || 0
          });
        }
      }
      
      alert(`${isAccepted ? 'Accepted' : 'Declined'} vendor staff request.`);
      setIsProcessing(false);
      return; // Exit early since we've handled this notification type
    } catch (error) {
      console.error(`Error ${isAccepted ? 'accepting' : 'declining'} vendor staff request:`, error);
      alert(`Failed to ${isAccepted ? 'accept' : 'decline'} vendor staff request. Please try again.`);
      setIsProcessing(false);
      return; // Exit early to prevent further processing
    }
  }

    
    console.log("Processing staff request notification:", notification);
    
    
    const db = getFirestoreInstance();
    const vendorId = notification.organizationId; // This is the vendor's userId
    const isPast = notification.isPast || false;
    
    // Extract permissions and date information from the notification
    const permissions = notification.permissions || {};
    let dateInfo;
    
    if (isPast) {
      dateInfo = {
        duration: notification.data?._displayData?.duration || {
          startMonth: notification.data?._displayData?.startMonth,
          startYear: notification.data?._displayData?.startYear,
          endMonth: notification.data?._displayData?.endMonth,
          endYear: notification.data?._displayData?.endYear
        }
      };
    } else {
      dateInfo = {
        since: notification.data?._displayData?.since || {
          month: notification.data?._displayData?.sinceMonth,
          year: notification.data?._displayData?.sinceYear
        }
      };
    }
    
    if (isAccepted) {
      // ACCEPTANCE FLOW
      
      // 1. Get the pending request from the vendor's pendingVendorStaff subcollection
      let requestId = notification.requestId || notification.data?.staffData?.staffId;
      let pendingRequest = null;
      
      if (requestId) {
        const pendingRequestRef = doc(db, 'users', vendorId, isPast ? 'pendingPastVendorStaff' : 'pendingVendorStaff', requestId);
        const pendingRequestDoc = await getDoc(pendingRequestRef);
        
        if (pendingRequestDoc.exists()) {
          pendingRequest = {
            id: pendingRequestDoc.id,
            ...pendingRequestDoc.data()
          };
        }
      }
      
      // 2. Move from pending to vendorStaff subcollection
      const staffData = {
        userId: userData.userId,
        status: 'accepted',
        permissions: permissions,
        isPast: isPast,
        createdAt: pendingRequest?.createdAt || new Date().toISOString(),
        acceptedAt: new Date().toISOString(),
        ...(isPast ? { duration: dateInfo.duration } : { since: dateInfo.since })
      };
      
      // Create the staff record in the vendorStaff subcollection
      const vendorStaffRef = collection(db, 'users', vendorId, 'vendorStaff');
      const staffDocRef = await addDoc(vendorStaffRef, staffData);
      
      // Store the ID of the new record to link both sides
      const staffDocId = staffDocRef.id;
      
      // 3. Create entry in staff's associatedVendors subcollection
      const staffAssociatedVendorData = {
        vendorId: vendorId,
        status: 'active',
        permissions: permissions,
        isPast: isPast,
        createdAt: pendingRequest?.createdAt || new Date().toISOString(),
        acceptedAt: new Date().toISOString(),
        otherSideDocId: staffDocId, // Link to the vendor's document
        ...(isPast ? { duration: dateInfo.duration } : { since: dateInfo.since })
      };
      
      // Add to staff's associatedVendors subcollection
      const associatedVendorsRef = collection(db, 'users', userData.userId, 'associatedVendors');
      const associatedVendorDocRef = await addDoc(associatedVendorsRef, staffAssociatedVendorData);
      
      // Update the vendor's staff document with the reference to the staff's document
      await updateDoc(doc(db, 'users', vendorId, 'vendorStaff', staffDocId), {
        otherSideDocId: associatedVendorDocRef.id
      });
      
      // 4. Get vendor data for updating arrays in main documents
      const vendorDoc = await getDoc(doc(db, 'users', vendorId));
      let vendorData = {};
      
      if (vendorDoc.exists()) {
        vendorData = vendorDoc.data();
      } else {
        throw new Error('Vendor not found');
      }
      
      // 5. Update vendor's main document with top 3 staff
      // First get the top 3 staff from the subcollection
      const top3Query = query(
        vendorStaffRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const top3Snapshot = await getDocs(top3Query);
      const top3Staff = await Promise.all(
        top3Snapshot.docs.map(async (docSnapshot) => {
          const staffData = docSnapshot.data();
          
          // Enrich with user data
          if (staffData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', staffData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...staffData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${staffData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...staffData
          };
        })
      );
      
      // Update the vendor's main document
      await updateDoc(doc(db, 'users', vendorId), {
        [isPast ? 'pastVendorStaff' : 'vendorStaff']: top3Staff,
        [isPast ? 'pastVendorStaffCount' : 'vendorStaffCount']: increment(1)
      });
      
      // 6. Update staff's main document with top 3 vendors
      // First get the top 3 vendors from the subcollection
      const top3VendorsQuery = query(
        associatedVendorsRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const top3VendorsSnapshot = await getDocs(top3VendorsQuery);
      const top3Vendors = await Promise.all(
        top3VendorsSnapshot.docs.map(async (docSnapshot) => {
          const vendorData = docSnapshot.data();
          
          // Enrich with vendor data
          if (vendorData.vendorId) {
            try {
              const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
              if (vendorDoc.exists()) {
                const vendorUserData = vendorDoc.data();
                return {
                  id: docSnapshot.id,
                  ...vendorData,
                  fullName: vendorUserData.fullName || '',
                  userName: vendorUserData.userName || '',
                  profilePictureUrl: vendorUserData.profilePictureUrl || '',
                  userSubType: vendorUserData.userSubType || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...vendorData
          };
        })
      );
      
      // Update staff's main document
      await updateDoc(doc(db, 'users', userData.userId), {
        [isPast ? 'pastAssociatedVendors' : 'associatedVendors']: top3Vendors,
        [isPast ? 'pastAssociatedVendorsCount' : 'associatedVendorsCount']: increment(1)
      });
      
      // 7. Remove the pending request
      if (requestId) {
        await deleteDoc(doc(db, 'users', vendorId, isPast ? 'pendingPastVendorStaff' : 'pendingVendorStaff', requestId));
      }
      
      // 8. Send acceptance notification to vendor
      await createNotification(vendorId, 'general', {
        type: isPast ? NOTIFICATION_TYPES.PAST_VENDOR_STAFF_ACCEPTED : NOTIFICATION_TYPES.VENDOR_STAFF_ACCEPTED,
        title: `${isPast ? 'Past ' : ''}Staff Request Accepted`,
        message: `${userData.fullName} has accepted your ${isPast ? 'past ' : ''}staff request.`,
        userId: userData.userId,
        status: 'accepted',
        timestamp: new Date().toISOString(),
        data: {
          _displayData: {
            userName: userData.userName,
            fullName: userData.fullName,
            profilePictureUrl: userData.profilePictureUrl || ''
          }
        }
      });
      
      // 9. Mark notification as accepted
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'accepted', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'accepted', read: true } 
          : n
      );
      
      // Find and update notification in user's subcollection
      await markNotificationAsProcessed(userData.userId, notification.id, 'accepted');
      
      alert(`${isPast ? 'Past ' : ''}Staff request accepted successfully!`);
    } else {
      // REJECTION FLOW
      
      // 1. Get the request ID
      const requestId = notification.requestId || notification.data?.staffData?.staffId;
      
      // 2. Remove the pending request if it exists
      if (requestId) {
        await deleteDoc(doc(db, 'users', vendorId, isPast ? 'pendingPastVendorStaff' : 'pendingVendorStaff', requestId));
      }
      
      // 3. Send rejection notification to vendor
      await createNotification(vendorId, 'general', {
        type: isPast ? NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REJECTED : NOTIFICATION_TYPES.VENDOR_STAFF_REJECTED,
        title: `${isPast ? 'Past ' : ''}Staff Request Declined`,
        message: `${userData.fullName} has declined your ${isPast ? 'past ' : ''}staff request.`,
        userId: userData.userId,
        status: 'rejected',
        timestamp: new Date().toISOString(),
        data: {
          _displayData: {
            userName: userData.userName,
            fullName: userData.fullName,
            profilePictureUrl: userData.profilePictureUrl || ''
          }
        }
      });
      
      // 4. Mark notification as rejected
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'rejected', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'rejected', read: true } 
          : n
      );
      
      // Find and update notification in user's subcollection
      await markNotificationAsProcessed(userData.userId, notification.id, 'rejected');
      
      alert(`${isPast ? 'Past ' : ''}Staff request declined.`);
    }
    
    // Refresh notifications
    fetchNotifications(true);
  } catch (error) {
    console.error('Error handling staff request:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} request. ${error.message}`);
  } finally {
    setIsProcessing(false);
  }
};

const handleAssociatedVendorRequest = async (notification, isAccepted) => {
  try {
    setIsProcessing(true);
    const db = getFirestoreInstance();
    
    const requestData = notification.data?.requestData;
    if (!requestData) {
      throw new Error('Request data is missing');
    }
    
    const requesterId = requestData.requestedBy?.userId;
    const requesterUserType = requestData.requestedBy?.userType;
    const isPastRequest = requestData.isPast || false;
    
    if (isAccepted) {
      // Accept the associated vendor request
      
      // 1. Add to requester's associatedVendors subcollection
      const associatedVendorsRef = collection(db, 'users', requesterId, 
        isPastRequest ? 'pastAssociatedVendors' : 'associatedVendors');
      
      const newVendorDoc = await addDoc(associatedVendorsRef, {
        vendorId: userData.userId,
        status: 'accepted',
        isPast: isPastRequest,
        ...(isPastRequest ? 
          { duration: requestData.duration } : 
          { since: requestData.since }
        ),
        createdAt: new Date().toISOString(),
        acceptedAt: new Date().toISOString(),
        associationId: requestData.associationId
      });
      
      // 2. Add the requester to the current user's vendorStaff subcollection
      const staffRef = collection(db, 'users', userData.userId, isPastRequest ? 'pastVendorStaff' : 'vendorStaff');
      const staffDoc = await addDoc(staffRef, {
        userId: requesterId,
        status: 'accepted',
        isPast: isPastRequest,
        ...(isPastRequest ? 
          { duration: requestData.duration } : 
          { since: requestData.since }
        ),
        createdAt: new Date().toISOString(),
        acceptedAt: new Date().toISOString(),
        otherSideDocId: newVendorDoc.id // Store the ID of the other side document
      });
      
      // Update the other side document with a reference to this one
      await updateDoc(newVendorDoc, {
        otherSideDocId: staffDoc.id
      });
      
      // 3. Update top 3 staff in the vendor's main document
      const vendorRef = doc(db, 'users', userData.userId);
      const vendorDoc = await getDoc(vendorRef);
      
      if (vendorDoc.exists()) {
        const staffCollectionRef = collection(db, 'users', userData.userId, isPastRequest ? 'pastVendorStaff' : 'vendorStaff');
        const top3Query = query(
          staffCollectionRef,
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3Staff = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const staffData = docSnapshot.data();
            
            // Enrich with user data
            if (staffData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...staffData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${staffData.userId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...staffData
            };
          })
        );
        
        // Update vendor document
        await updateDoc(vendorRef, {
          [isPastRequest ? 'pastVendorStaff' : 'vendorStaff']: top3Staff,
          [isPastRequest ? 'pastVendorStaffCount' : 'vendorStaffCount']: increment(1)
        });
      }
      
      // 4. Update top 3 vendors in the staff's main document
      const staffUserRef = doc(db, 'users', requesterId);
      const staffUserDoc = await getDoc(staffUserRef);
      
      if (staffUserDoc.exists()) {
        const vendorsCollectionRef = collection(db, 'users', requesterId, isPastRequest ? 'pastAssociatedVendors' : 'associatedVendors');
        const top3VendorsQuery = query(
          vendorsCollectionRef,
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3VendorsSnapshot = await getDocs(top3VendorsQuery);
        const top3Vendors = await Promise.all(
          top3VendorsSnapshot.docs.map(async (docSnapshot) => {
            const vendorData = docSnapshot.data();
            
            // Enrich with vendor data
            if (vendorData.vendorId) {
              try {
                const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                if (vendorDoc.exists()) {
                  const vendorUserData = vendorDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...vendorData,
                    fullName: vendorUserData.fullName || '',
                    userName: vendorUserData.userName || '',
                    profilePictureUrl: vendorUserData.profilePictureUrl || '',
                    userSubType: vendorUserData.userSubType || '',
                    idVerificationStatus: vendorUserData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...vendorData
            };
          })
        );
        
        // Update staff document
        await updateDoc(staffUserRef, {
          [isPastRequest ? 'pastAssociatedVendors' : 'associatedVendors']: top3Vendors,
          [isPastRequest ? 'pastAssociatedVendorsCount' : 'associatedVendorsCount']: increment(1)
        });
      }
      
      // 5. Delete the pending request document if it exists
      if (requestData.requestDocId) {
        try {
          const pendingRequestRef = doc(db, 'users', requesterId, 
            isPastRequest ? 'pendingPastAssociatedVendors' : 'pendingAssociatedVendors', 
            requestData.requestDocId);
          await deleteDoc(pendingRequestRef);
        } catch (error) {
          console.error('Error deleting pending request:', error);
          // Continue even if this fails
        }
      }
      
      
      // 6. Send acceptance notification to staff
      await createNotification(requesterId, 'general', {
        type: isPastRequest ? NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_ACCEPTED : NOTIFICATION_TYPES.ASSOCIATED_VENDOR_ACCEPTED,
        title: isPastRequest ? 'Past Associated Vendor Request Accepted' : 'Associated Vendor Request Accepted',
        message: `${userData.fullName} has accepted your request to add them as a ${isPastRequest ? 'past' : 'current'} vendor.`,
        data: {
          vendorId: userData.userId,
          isPast: isPastRequest,
          _displayData: {
            userName: userData.userName,
            fullName: userData.fullName
          }
        }
      });
      
      // 7. Mark the notification as accepted
      await markNotificationAsProcessed(userData.userId, notification.id, 'accepted');

      // After successfully accepting the request, refresh user data
      await refreshNotifications(userData.userId, true);

       // After successfully accepting the request, refresh user data
       if (userData && setUserData) {
        // Get fresh user data
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        if (userDoc.exists()) {
          const freshUserData = userDoc.data();
          // Update user context with fresh data
          setUserData({
            ...userData,
            ...freshUserData,
            // Ensure we're updating the specific fields that StaffSection and AssociatedVendorsSection use
            associatedVendors: freshUserData.associatedVendors || [],
            pastAssociatedVendors: freshUserData.pastAssociatedVendors || [],
            associatedVendorsCount: freshUserData.associatedVendorsCount || 0,
            pastAssociatedVendorsCount: freshUserData.pastAssociatedVendorsCount || 0,
            vendorStaff: freshUserData.vendorStaff || [],
            pastVendorStaff: freshUserData.pastVendorStaff || [],
            vendorStaffCount: freshUserData.vendorStaffCount || 0,
            pastVendorStaffCount: freshUserData.pastVendorStaffCount || 0
          });
        }
      }
      
      // Update UI
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'accepted', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'accepted', read: true } 
          : n
      );
      
      alert('You have successfully accepted the associated vendor request.');
      
    } else {
      // Reject the associated vendor request
      
      // 1. Delete the pending request if it exists
      if (requestData.requestDocId) {
        try {
          const pendingRequestRef = doc(db, 'users', requesterId, 
            isPastRequest ? 'pendingPastAssociatedVendors' : 'pendingAssociatedVendors', 
            requestData.requestDocId);
          await deleteDoc(pendingRequestRef);
        } catch (error) {
          console.error('Error deleting pending request:', error);
          // Continue even if this fails
        }
      }
      
      // 2. Send rejection notification to staff
      await createNotification(requesterId, 'general', {
        type: isPastRequest ? NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REJECTED : NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REJECTED,
        title: isPastRequest ? 'Past Associated Vendor Request Declined' : 'Associated Vendor Request Declined',
        message: `${userData.fullName} has declined your request to add them as a ${isPastRequest ? 'past' : 'current'} vendor.`,
        data: {
          vendorId: userData.userId,
          isPast: isPastRequest,
          _displayData: {
            userName: userData.userName,
            fullName: userData.fullName
          }
        }
      });
      
      // 3. Mark the notification as rejected
      await markNotificationAsProcessed(userData.userId, notification.id, 'rejected');
      
      // Update UI
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'rejected', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'rejected', read: true } 
          : n
      );
      
      alert('You have declined the associated vendor request.');
    }
    
    
    await refreshNotifications(userData.userId, true);
      
       // Even for rejection, update the UI
       if (userData && setUserData) {
        // Get fresh user data
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        if (userDoc.exists()) {
          const freshUserData = userDoc.data();
          setUserData({
            ...userData,
            ...freshUserData
          });
        }
      }
    
    // Refresh notifications
    fetchNotifications(true);
    
  } catch (error) {
    console.error('Error handling associated vendor request:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} request. Please try again.`);
  } finally {
    setIsProcessing(false);
  }
};

// Updated handleVendorRequestAction function
const handleVendorRequestAction = async (notification, isAccepted) => {
try {
setIsProcessing(true);
const db = getFirestoreInstance();
const { eventId, tourId, eventDateId, tourDateId } = notification;
const actualEventId = eventId || tourId;
const actualDateId = eventDateId || tourDateId;
const eventType = notification.eventType || 'tour';

// Get the main event document
const collectionName = eventType === 'tour' ? 'tours' : 'shows';
const eventRef = doc(db, collectionName, actualEventId);
const eventDoc = await getDoc(eventRef);
const eventData = eventDoc.data();

// Get the vendor details array
const vendorDetails = eventData.vendorDetails || [];

// Find the vendor entry
const vendorIndex = vendorDetails.findIndex(v => v.userId === userData.userId);

if (vendorIndex >= 0) {
  // Find the specific assigned date
  const assignedDates = vendorDetails[vendorIndex].assignedDates || [];
  const dateIndex = assignedDates.findIndex(d => d.dateDocId === actualDateId);
  
  if (dateIndex >= 0) {
    // Update the date status
    assignedDates[dateIndex].status = isAccepted ? 'accepted' : 'rejected';
    vendorDetails[vendorIndex].assignedDates = assignedDates;
    
    // Update the event document
    await updateDoc(eventRef, {
      vendorDetails: vendorDetails
    });
    
    if (isAccepted) {
      // Add event to user's assignedEvents subcollection
      const eventDocRef = doc(db, 'users', userData.userId, 'assignedEvents', actualEventId);
      await setDoc(eventDocRef, {
        eventId: actualEventId,
        eventType: eventType,
        createdAt: serverTimestamp()
      });
      
      // Add to user_events collection
      try {
        // First check if the user_events document exists
        const userEventRef = doc(db, 'user_events', userData.userId, 'events', actualEventId);
        const userEventDoc = await getDoc(userEventRef);
        
        if (userEventDoc.exists()) {
          // Update lastUpdated timestamp
          await updateDoc(userEventRef, {
            lastUpdated: serverTimestamp()
          });
        } else {
          // Create the event document if it doesn't exist
          await setDoc(userEventRef, {
            eventId: actualEventId,
            eventType: eventType,
            tourName: eventData.tourName,
            showName: eventType === 'show' ? eventData.showName || eventData.tourName : null,
            actName: eventData.actName,
            coverPhotoUrl: eventData.tourCoverPhotoUrl || '',
            userRole: 'Vendor',
            userType: 'Vendor',
            userSubType: userData.userSubType,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
        
        // Get the event date data
        const dateRef = doc(db, collectionName, actualEventId, 'eventDates', actualDateId);
        const dateDoc = await getDoc(dateRef);
        
        if (dateDoc.exists()) {
          const dateData = {
            id: actualDateId,
            ...dateDoc.data()
          };
          
          const venueData = dateData.event?.venueDetails?.venueData || {};
          
          // Create/update the event date document in user_events
          const userEventDateRef = doc(db, 'user_events', userData.userId, 'events', actualEventId, 'eventDates', actualDateId);
          await setDoc(userEventDateRef, {
            date: normalizeDate(dateData.date), // Normalize the date
            dateDocId: actualDateId,
            verified: false,
            venueDetails: venueData,
            userRole: 'Vendor',
            userType: 'Vendor',
            userSubType: userData.userSubType,
            status: 'accepted',
            eventType: eventType,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error updating user_events collection for vendor:', error);
        // Continue with the process even if this update fails
      }
      
      // Add to user_event_dates collection
      try {
        // Get the event date data
        const dateRef = doc(db, collectionName, actualEventId, 'eventDates', actualDateId);
        const dateDoc = await getDoc(dateRef);
        
        if (dateDoc.exists()) {
          const dateData = {
            id: actualDateId,
            ...dateDoc.data(),
            eventType: eventType
          };
          
          // Normalize the date format
          if (dateData.date) {
            dateData.date = normalizeDate(dateData.date);
          }
          
          const venueData = dateData.event?.venueDetails?.venueData || {};
          
          // Get user's map privacy setting
          let mapPrivacy = "public"; // Default
          try {
            const userDoc = await getDoc(doc(db, 'users', userData.userId));
            if (userDoc.exists()) {
              mapPrivacy = userDoc.data().mapPrivacy || "public";
            }
          } catch (err) {
            console.error('Error getting user map privacy:', err);
          }
          
          // Update user_event_dates
          await updateUserEventDate(
            userData.userId,
            actualEventId,
            actualDateId,
            dateData,
            {
              userId: userData.userId,
              fullName: userData.fullName,
              userName: userData.userName,
              profilePictureUrl: userData.profilePictureUrl,
              userType: 'Vendor',
              userSubType: userData.userSubType,
              mapPrivacy: mapPrivacy
            }, 
            venueData,
            'accepted'
          );
        }
      } catch (syncError) {
        console.error('Error updating user_event_dates for vendor:', syncError);
        // Continue even if sync fails
      }
    }
    
    // Update local notification state
    setNotifications(prev => 
      prev.map(n => 
        n.id === notification.id 
          ? { ...n, status: isAccepted ? 'accepted' : 'rejected', read: true } 
          : n
      )
    );
    
    // Also update the notifications cache
    allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
      n.id === notification.id 
        ? { ...n, status: isAccepted ? 'accepted' : 'rejected', read: true } 
        : n
    );
    
    // Mark notification as processed
    await markNotificationAsProcessed(userData.userId, notification.id, isAccepted ? 'accepted' : 'rejected');
    
    // Refresh notifications
    fetchNotifications(true);
    
    // Force a refresh of the event context data
    if (updateEventData) {
      await updateEventData(actualEventId, eventType);
    }
  }
}
} catch (error) {
console.error('Error handling vendor request:', error);
alert(`Failed to ${isAccepted ? 'accept' : 'decline'} vendor request. Please try again.`);
} finally {
setIsProcessing(false);
}
};
// Function to handle band event invitation acceptance/rejection
const handleBandEventInvitationAction = async (notification, isAccepted) => {
try {
setIsProcessing(true);
if (isAccepted) {
  // Accept invitation
  await acceptBandEventInvitation(notification, userData);
  
  // Update local state
  setBandNotifications(prev => 
    prev.map(n => 
      n.id === notification.id 
        ? { ...n, status: 'accepted', readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } }
        : n
    )
  );
  
  // Also update the notifications cache
  allNotificationsRef.current.bands = allNotificationsRef.current.bands.map(n => 
    n.id === notification.id 
      ? { ...n, status: 'accepted', readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } }
      : n
  );
  
  // Show success message
  alert('Event invitation accepted successfully!');
} else {
  // Reject invitation
  await rejectBandEventInvitation(notification, userData);
  
  // Update local state
  setBandNotifications(prev => 
    prev.map(n => 
      n.id === notification.id 
        ? { ...n, status: 'rejected', readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } }
        : n
    )
  );
  
  // Also update the notifications cache
  allNotificationsRef.current.bands = allNotificationsRef.current.bands.map(n => 
    n.id === notification.id 
      ? { ...n, status: 'rejected', readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } }
      : n
  );
  
  // Show message
  alert('Event invitation declined.');
}

// Refresh notifications
fetchNotifications(true);

// Force a refresh of band data
if (refreshBandData) {
  await refreshBandData();
}
} catch (error) {
console.error('Error handling band event invitation:', error);
alert(`Failed to ${isAccepted ? 'accept' : 'decline'} invitation. Please try again.`);
  } finally {
setIsProcessing(false);
}
};
/**

Navigate to a band profile by band ID
@param {string} bandId - The band ID
@returns {Promise<boolean>} Success status
*/
const navigateToBandProfile = async (bandId) => {
try {
const db = getFirestoreInstance();
const bandDoc = await getDoc(doc(db, 'bands', bandId));
if (bandDoc.exists()) {
const bandData = bandDoc.data();
if (bandData.bandUserName) {
  navigate(`/bands/${bandData.bandUserName}`);
onClose();
return true;
} else {
// Fallback to ID-based navigation if username is missing
navigate(`/bands/${bandId}`);
onClose();
return true;
}
} else {
console.error('Band not found:', bandId);
alert('Could not find this band profile.');
return false;
}
} catch (error) {
console.error('Error navigating to band profile:', error);
alert('Error loading band profile.');
return false;
}
};

// Add handleManagerRequest function to handle manager requests
const handleManagerRequest = async (notification, isAccepted) => {
  try {
    setIsProcessing(true);
    const db = getFirestoreInstance();
    
    if (isAccepted) {
      // Get the organization ID and request document ID
      const organizationId = notification.organizationId;
      const requestId = notification.data?.actData?.actId || notification.data?.requestId;
      const userId = notification.userId || userData.userId;
      
      if (organizationId && requestId) {
        // Move from pendingManagingActs to managingActs
        try {
          await movePendingToManagingAct(organizationId, userId, requestId);
        } catch (error) {
          console.error('Error moving manager from pending to managed:', error);
          // Continue even if this fails
        }
      }
      
      // Accept the manager request
      await acceptManagerRequest(notification.id, userData.userId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'accepted', read: true } 
            : n
        )
      );
      
      // Update cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'accepted', read: true } 
          : n
      );
      
      alert('You have successfully accepted the manager request.');
    } else {
      // Handle rejection
      const organizationId = notification.organizationId;
      const requestId = notification.data?.actData?.actId || notification.data?.requestId;
      
      // If we have a pending request ID, delete it
      if (organizationId && requestId) {
        try {
          const pendingActRef = doc(db, 'users', organizationId, 'pendingManagingActs', requestId);
          await deleteDoc(pendingActRef);
        } catch (error) {
          console.error('Error deleting pending request:', error);
          // Continue even if this fails
        }
      }
      
      // Reject the manager request
      await rejectManagerRequest(notification.id, userData.userId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'rejected', read: true } 
            : n
        )
      );
      
      // Update cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'rejected', read: true } 
          : n
      );
      
      alert('You have declined the manager request.');
    }
    
    // Refresh notifications
    fetchNotifications(true);
  } catch (error) {
    console.error('Error handling manager request:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} manager request. Please try again.`);
  } finally {
    setIsProcessing(false);
  }
};

// Add handleArtistRequest function to handle artist requests
const handleArtistRequest = async (notification, isAccepted) => {
  try {
    setIsProcessing(true);
    const db = getFirestoreInstance();
    
    if (isAccepted) {
      // Get the organization ID and request document ID
      const organizationId = notification.organizationId;
      const requestId = notification.data?.actData?.actId || notification.data?.requestId;
      const userId = notification.userId || userData.userId;
      
      if (!organizationId || !requestId) {
        throw new Error('Missing organization or request information');
      }
      
      // Move from pendingManagingActs to managingActs
      await movePendingToManagingAct(organizationId, userId, requestId);
      
      // Update existing artist's record to accepted
      await acceptArtistRequest(notification.id, userData.userId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'accepted', read: true } 
            : n
        )
      );
      
      // Update cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'accepted', read: true } 
          : n
      );
      
      alert('You have successfully accepted the artist request.');
    } else {
      // Handle rejection
      const organizationId = notification.organizationId;
      const requestId = notification.data?.actData?.actId || notification.data?.requestId;
      
      // If we have a pending request ID, delete it
      if (organizationId && requestId) {
        try {
          const pendingActRef = doc(db, 'users', organizationId, 'pendingManagingActs', requestId);
          await deleteDoc(pendingActRef);
        } catch (error) {
          console.error('Error deleting pending request:', error);
          // Continue even if this fails
        }
      }
      
      // Reject the artist request
      await rejectArtistRequest(notification.id, userData.userId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'rejected', read: true } 
            : n
        )
      );
      
      // Update cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id 
          ? { ...n, status: 'rejected', read: true } 
          : n
      );
      
      alert('You have declined the artist request.');
    }
    
    // Refresh notifications
    fetchNotifications(true);
  } catch (error) {
    console.error('Error handling artist request:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} artist request. Please try again.`);
  } finally {
    setIsProcessing(false);
  }
};

const handleNotificationClick = async (notification) => {
try {
// 1. Mark notification as read based on its type
if (activeTab === 'bands') {
// Handle band notification read status
if (!isBandNotificationRead(notification, userData.userId)) {
const bandId = notification.bandInfo?.id;
if (bandId) {
await markBandNotificationAsRead(bandId, notification.id, userData.userId);
        // Update local state
        setBandNotifications(prev => 
          prev.map(n => 
            n.id === notification.id ? 
              { ...n, readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } } : 
              n
          )
        );
  
        // Update all notifications cache
        allNotificationsRef.current.bands = allNotificationsRef.current.bands.map(n => 
          n.id === notification.id ? 
            { ...n, readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } } : 
            n
        );
  
        // Update unread count
        setUnreadCount(prev => ({
          ...prev,
          bands: Math.max(0, prev.bands - 1)
        }));
      }
    }
  } else {
    // Handle general or invitation notification read status
    if (!notification.read) {
      await markAsRead(userData.userId, [notification.id], activeTab);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        )
      );

      // Update all notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      );

      // Update unread count
      setUnreadCount(prev => ({
        ...prev,
        [activeTab]: Math.max(0, prev[activeTab] - 1)
      }));
    }
  }

  // 2. Update UI state based on notification type
  if (userData && processNotificationUIUpdates) {
    await processNotificationUIUpdates(
      notification, 
      userData, 
      { 
        updateBandMembership, 
        refreshBandData,
        setUserData 
      }
    );
  }
  
  // 3. Special handling for connection requests - directly navigate to user profile
  if (notification.type === 'CONNECTION_REQUEST') {
    const senderUserName = notification.data?._displayData?.userName;
    const senderUserId = notification.data?.senderUserId;
    
    if (senderUserName) {
      // If we have the username, navigate directly to profile
      navigate(`/${senderUserName}`);
      onClose();
      return;
    } else if (senderUserId) {
      // If we only have the user ID, get the username first
      await navigateToUserProfile(senderUserId);
      return;
    }
  }

   // IMPORTANT: Add handling for task-related notifications
   if (notification.type === 'TASK_ASSIGNED' || 
   notification.type === 'TASK_COMPLETED' || 
   notification.type === 'TASK_ACCEPTED' || 
   notification.type === 'TASK_REJECTED' ||
   notification.type === 'TASK_RESPONSE') {
 
  const eventId = notification.data?.eventId;
  const dateId = notification.data?.dateId;
  const eventType = notification.data?.eventType || 'tour';
  
  if (eventId && dateId) {
    // Force update event data if needed
    if (updateEventData) {
      await updateEventData(eventId, eventType);
    }
    
    // FIXED: Generate the correct route URL without "/dates/" segment
    // This matches the path defined in App.js routes
    const route = `/${eventType}/${eventId}/${dateId}/tasks?fromNotification=true`;
    
    console.log(`Navigating to task from notification: ${route}`);
    navigate(route);
    onClose();
    return;
  }
}
  
  // 4. Show notification details in a modal for specific notification types
  if (
    notification.type === NOTIFICATION_TYPES.TOUR_INVITATION || 
    notification.type === NOTIFICATION_TYPES.SHOW_INVITATION ||
    notification.type === 'STAFF_REQUEST' || 
    notification.type === 'VENDOR_REQUEST' ||
    notification.type === NOTIFICATION_TYPES.TASK_ASSIGNED ||
    notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST ||
  notification.type === NOTIFICATION_TYPES.ORGARTIST_REQUEST ||
  notification.type === NOTIFICATION_TYPES.ASSOCIATION_REQUEST ||
  notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST || 
  notification.type === NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST
  ) {
    setSelectedNotification(notification);
    setIsModalOpen(true);
    return; // Stop here - don't navigate yet
  }

  // 5. Handle navigation based on notification type
  // Get the appropriate band identifier and user identifier
  const bandId = notification.bandInfo?.id || notification.data?.bandId;
  const userId = notification.data?.senderUserId || notification.data?.targetUserId || 
                notification.data?.userId || notification.actionBy?.userId;
  const eventId = notification.data?.eventId || notification.data?.tourId;
  const eventDateId = notification.data?.eventDateId || notification.data?.dateId;
  const eventType = notification.data?.eventType || 'tour';
  
  // Navigate based on notification type
  switch (notification.type) {
    case NOTIFICATION_TYPES.BAND_CREATED:
    case NOTIFICATION_TYPES.BAND_UPDATED:
    case NOTIFICATION_TYPES.BAND_DELETED:
    case NOTIFICATION_TYPES.ADMIN_ADDED:
    case NOTIFICATION_TYPES.ADMIN_REMOVED:
    case NOTIFICATION_TYPES.USER_ADDED:
    case NOTIFICATION_TYPES.USER_REMOVED:
    case NOTIFICATION_TYPES.MEMBER_REQUEST:
    case NOTIFICATION_TYPES.REQUEST_ACCEPTED:
    case NOTIFICATION_TYPES.REQUEST_REJECTED:
      // Navigate to band profile using ID instead of stored username
      if (bandId) {
        await navigateToBandProfile(bandId);
      }
      break;
      
    case NOTIFICATION_TYPES.RECOMMENDATION_REQUEST:
      // Navigate to user profile using ID instead of stored username
      if (userId) {
        await navigateToUserProfile(userId);
      }
      break;
      
    case NOTIFICATION_TYPES.TASK_ASSIGNED:
      if (eventId && eventDateId) {
        await updateEventData(eventId, eventType);
        const route = eventType === 'tour' ? 
          `/tour/${eventId}/${eventDateId}/tasks` : 
          `/show/${eventId}/tasks`;
        navigate(route);
        onClose();
      }
      break;
      
    case NOTIFICATION_TYPES.TOUR_INVITATION:
    case NOTIFICATION_TYPES.SHOW_INVITATION:
    case NOTIFICATION_TYPES.TOUR_DATE_REMOVAL:
    case NOTIFICATION_TYPES.SHOW_REMOVAL:
      if (eventId) {
        await updateEventData(eventId, eventType);
        const route = eventType === 'tour' ? 
          `/tour/${eventId}` : 
          `/show/${eventId}`;
        navigate(route);
        onClose();
      }
      break;
      
    case NOTIFICATION_TYPES.TOUR_DATE_RESCHEDULED:
    case NOTIFICATION_TYPES.SHOW_RESCHEDULED:
    case NOTIFICATION_TYPES.TOUR_DATE_CANCELLED:
    case NOTIFICATION_TYPES.SHOW_CANCELLED:
    case NOTIFICATION_TYPES.TOUR_DATE_UNCANCELLED:
    case NOTIFICATION_TYPES.SHOW_UNCANCELLED:
      if (eventId && eventDateId) {
        await updateEventData(eventId, eventType);
        const route = eventType === 'tour' ? 
          `/tour/${eventId}/${eventDateId}` : 
          `/show/${eventId}`;
        navigate(route);
        onClose();
      } else if (eventId) {
        await updateEventData(eventId, eventType);
        const route = eventType === 'tour' ? 
          `/tour/${eventId}` : 
          `/show/${eventId}`;
        navigate(route);
        onClose();
      }
      break;

      case 'TASK_ASSIGNED':
    case 'TASK_COMPLETED':
    case 'TASK_ACCEPTED':
    case 'TASK_REJECTED':
    case 'TASK_RESPONSE':
      return {
        icon: getNotificationIcon(notification),
        content: (
          <div className={styles.notificationContent}>
            <div className={styles.notificationHeader}>
              <h3>{notification.title}</h3>
              <span className={styles.timestamp}>
                {formatTimeAgo(notification.timestamp)}
              </span>
            </div>
            <p>{notification.message}</p>
            {notification.data?._displayData?.taskTitle && (
              <div className={styles.taskInfo}>
                <span className={styles.taskTitle}>
                  Task: {notification.data._displayData.taskTitle}
                </span>
              </div>
            )}
            {notification.data?._displayData?.eventName && (
              <span className={styles.eventName}>
                {notification.data._displayData.eventName}
              </span>
            )}
          </div>
        )
      };
      break;
      case 'EARNING_RECORDED':
case 'EXPENSE_OWED':
case 'EXPENSE_SETTLED':
  if (notification.data?.eventId) {
    const eventId = notification.data.eventId;
    const eventType = notification.data.eventType || 'tour';
    const dateValue = notification.data.date;
    
    // First, navigate to the Expense page
    navigate('/Expense', { 
      state: { 
        fromNotification: true,
        notificationEventId: eventId,
        notificationEventType: eventType,
        notificationDateValue: dateValue
      }
    });
    onClose();
  }
  break;
  case 'NEW_FEEDBACK':
case 'FEEDBACK_UPDATED':
case 'FEEDBACK_REPLY':
  // Navigate to the user's profile and open the feedback section
  const feedbackProfileId = notification.data?.profileId || notification.data?.userId;
  const feedbackId = notification.data?.feedbackId;
  
  if (feedbackProfileId) {
    // First try to navigate using username if available
    const targetUserName = notification.data?._displayData?.userName;
    if (targetUserName) {
      navigate(`/${targetUserName}?section=feedback&id=${feedbackId || ''}`);
    } else {
      // Otherwise navigate by user ID
      await navigateToUserProfile(feedbackProfileId);
    }
    onClose();
  }
  break;

case 'NEW_REVIEW':
case 'REVIEW_UPDATED':
case 'REVIEW_REPLY':
  // Navigate to the user's profile and open the reviews section
  const reviewProfileId = notification.data?.profileId || notification.data?.userId;
  const reviewId = notification.data?.reviewId;
  
  if (reviewProfileId) {
    // First try to navigate using username if available
    const targetUserName = notification.data?._displayData?.userName;
    if (targetUserName) {
      navigate(`/${targetUserName}?section=reviews&id=${reviewId || ''}`);
    } else {
      // Otherwise navigate by user ID
      await navigateToUserProfile(reviewProfileId);
    }
    onClose();
  }
  break;
    default:
      // Default navigation based on available IDs
      if (bandId) {
        await navigateToBandProfile(bandId);
      } else if (userId) {
        await navigateToUserProfile(userId);
      } else if (eventId) {
        await updateEventData(eventId, eventType);
        const route = eventType === 'tour' ? 
          `/tour/${eventId}` : 
          `/show/${eventId}`;
        navigate(route);
        onClose();
      }
      break;
  }
} catch (error) {
  console.error('Error handling notification click:', error);
}
};
const getNotificationIcon = (notification) => {
switch (notification.type) {
  case 'TASK_ASSIGNED':
    return faTasks;
  case 'TASK_COMPLETED':
    return faCheckCircle;
  case 'TASK_ACCEPTED':
    return faCheck;
  case 'TASK_REJECTED':
    return faBan;
  case 'TASK_RESPONSE':
    return faComment;
case NOTIFICATION_TYPES.TOUR_INVITATION:
case NOTIFICATION_TYPES.SHOW_INVITATION:
return faUser;
case NOTIFICATION_TYPES.TOUR_DATE_REMOVAL:
case NOTIFICATION_TYPES.SHOW_REMOVAL:
return faCalendarAlt;
case NOTIFICATION_TYPES.TOUR_DATE_RESCHEDULED:
case NOTIFICATION_TYPES.SHOW_RESCHEDULED:
return faCalendarAlt;
case NOTIFICATION_TYPES.TOUR_DATE_CANCELLED:
case NOTIFICATION_TYPES.SHOW_CANCELLED:
return faTimesCircle;
case NOTIFICATION_TYPES.TOUR_DATE_UNCANCELLED:
case NOTIFICATION_TYPES.SHOW_UNCANCELLED:
return faCheckCircle;
case NOTIFICATION_TYPES.BAND_CREATED:
return faMusic;
case NOTIFICATION_TYPES.MEMBER_REQUEST:
return faUserPlus;
case NOTIFICATION_TYPES.REQUEST_ACCEPTED:
return faCheck;
case NOTIFICATION_TYPES.REQUEST_REJECTED:
return faBan;
case NOTIFICATION_TYPES.USER_ADDED:
return faUserPlus;
case NOTIFICATION_TYPES.USER_REMOVED:
return faUserMinus;
case NOTIFICATION_TYPES.BAND_UPDATED:
return faEdit;
case NOTIFICATION_TYPES.BAND_DELETED:
return faTrash;
case NOTIFICATION_TYPES.ADMIN_ADDED:
case NOTIFICATION_TYPES.ADMIN_REMOVED:
return faCog;
case NOTIFICATION_TYPES.RECOMMENDATION_REQUEST:
return faThumbsUp;
case 'VENDOR_REQUEST':
return faUsers;
case 'STAFF_REQUEST':
return faUsers;
case 'STAFF_ACCEPTED':
return faCheck;
case 'STAFF_REJECTED':
return faBan;
case 'STAFF_REMOVAL':
return faUserMinus;
case 'CONNECTION_REQUEST':
return faUserPlus;
case 'CONNECTION_ACCEPTED':
return faCheck;
case 'CONNECTION_REJECTED':
return faTimes;

case 'EARNING_RECORDED':
  return faCoins;
case 'EXPENSE_OWED':
  return faMoneyBill;
case 'EXPENSE_SETTLED':
  return faCheck;

  case 'NEW_FEEDBACK':
case 'FEEDBACK_UPDATED':
  return faComment;
case 'FEEDBACK_REPLY':
  return faReply;
case 'NEW_REVIEW':
case 'REVIEW_UPDATED':
  return faStar;
case 'REVIEW_REPLY':
  return faReply;

 case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REQUEST:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST:
  return faUserShield;
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_ACCEPTED:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_ACCEPTED:
  return faCheck;
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REJECTED:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REJECTED:
  return faTimes;
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REMOVED:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REMOVED:
  return faUserMinus;
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_MOVED_TO_PAST:
  return faArrowRight;

case NOTIFICATION_TYPES.ASSOCIATION_REQUEST:
  return faUserShield;
case NOTIFICATION_TYPES.ASSOCIATION_ACCEPTED:
  return faCheck;
case NOTIFICATION_TYPES.ASSOCIATION_REJECTED:
  return faTimes;

case NOTIFICATION_TYPES.ORGMANAGER_REQUEST:
case NOTIFICATION_TYPES.ORGMANAGER_ACCEPTED:
case NOTIFICATION_TYPES.ORGMANAGER_REJECTED:
case NOTIFICATION_TYPES.ORGMANAGER_REMOVAL:
  return faUser;
  
case NOTIFICATION_TYPES.ORGARTIST_REQUEST:
case NOTIFICATION_TYPES.ORGARTIST_ACCEPTED:
case NOTIFICATION_TYPES.ORGARTIST_REJECTED:
case NOTIFICATION_TYPES.ORGARTIST_REMOVAL:
  return faMicrophone;

  case NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST:
    case NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST:
      return faUserPlus;
    
    case NOTIFICATION_TYPES.VENDOR_STAFF_ACCEPTED:
    case NOTIFICATION_TYPES.PAST_VENDOR_STAFF_ACCEPTED:
      return faCheck;
      
    case NOTIFICATION_TYPES.VENDOR_STAFF_REJECTED:
    case NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REJECTED:
      return faTimes;
      
    case NOTIFICATION_TYPES.VENDOR_STAFF_REMOVAL:
    case NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REMOVAL:
      return faUserMinus;
      
    case NOTIFICATION_TYPES.VENDOR_STAFF_PERMISSIONS_UPDATED:
      return faEdit;
      
    case NOTIFICATION_TYPES.VENDOR_STAFF_MOVED_TO_PAST:
      return faArrowRight;
default:
return faInfo;
}
};
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  
  let date;
  try {
    // Handle different timestamp formats
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      // Timestamp-like object with seconds
      date = new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string') {
      // ISO string
      date = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      // Already a Date object
      date = timestamp;
    } else {
      // Fallback
      console.warn('Unknown timestamp format:', timestamp);
      return 'unknown date';
    }
    
    // Verify date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', timestamp);
      return 'invalid date';
    }
    
    // Calculate time difference
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
    
    // Format the date for older timestamps
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return 'date error';
  }
};

const getNotificationContent = (notification) => {
  if (activeTab === 'bands') {
    // Format band notification content with action buttons
    return {
      icon: getNotificationIcon(notification),
      content: (
        <div className={styles.notificationContent}>
          <div className={styles.notificationHeader}>
            <h3>{notification.title}</h3>
            <span className={styles.timestamp}>
              {formatTimeAgo(notification.timestamp)}
            </span>
          </div>
          <p>{notification.message}</p>
          {notification.bandInfo && (
            <span className={styles.bandName}>
              {notification.bandInfo.name}
            </span>
          )}
          {notification.actionBy && (
            <div className={styles.actionBy}>
              <img 
                src={notification.actionBy._displayData?.profilePictureUrl || defaultProfilePhoto} 
                alt={notification.actionBy._displayData?.fullName || "User"}
                className={styles.actionByPhoto}
              />
              <span>{notification.actionBy._displayData?.fullName || notification.actionBy.userId}</span>
            </div>
          )}
          
          {/* Add action buttons for specific notification types */}
          {notification.type === NOTIFICATION_TYPES.MEMBER_REQUEST && (
            <div className={styles.notificationActions}>
              <button 
                className={styles.acceptButton}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent click
                  handleBandRequestAction(notification, true);
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent click
                  handleBandRequestAction(notification, false);
                }}
              >
                Decline
              </button>
            </div>
          )}

          {/* Add band event invitation action buttons */}
          {(notification.type === 'TOUR_INVITATION' || notification.type === 'SHOW_INVITATION') && 
           (!notification.status || notification.status === 'pending') && 
           !isBandNotificationRead(notification, userData.userId) && (
            <div className={styles.notificationActions}>
              <button 
                className={styles.acceptButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBandEventInvitationAction(notification, true);
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBandEventInvitationAction(notification, false);
                }}
              >
                Decline
              </button>
            </div>
          )}

          {/* Show status messages for event invitation */}
          {(notification.type === 'TOUR_INVITATION' || notification.type === 'SHOW_INVITATION') && 
            notification.readBy && notification.readBy[userData.userId] && (
            <div className={styles.statusMessage}>
              <span className={notification.status === 'accepted' ? styles.acceptedStatus : styles.rejectedStatus}>
                {notification.status === 'accepted' ? 'You accepted this invitation' : 'You declined this invitation'}
              </span>
            </div>
          )}
        </div>
      )
    };
  } else {
    // Format general/invitation notification content
    return {
      icon: getNotificationIcon(notification),
      content: (
        <div className={styles.notificationContent}>
          <div className={styles.notificationHeader}>
            <h3>{notification.title}</h3>
            <span className={styles.timestamp}>
              {formatTimeAgo(notification.timestamp)}
            </span>
          </div>
          <p>{notification.message || notification.body}</p>
          
          {/* Recommendation request display */}
          {notification.type === NOTIFICATION_TYPES.RECOMMENDATION_REQUEST && (
            <div className={styles.actionBy}>
              <img 
                src={notification.data?._displayData?.senderProfilePictureUrl || defaultProfilePhoto} 
                alt={notification.data?._displayData?.senderFullName || "User"}
                className={styles.actionByPhoto}
              />
              <span>
                {notification.data?._displayData?.senderFullName || "User"}
              </span>
            </div>
          )}
          

          {/* Add action buttons for vendor requests */}
          {notification.type === 'VENDOR_REQUEST' && (!notification.status || notification.status === 'pending') && (
            <div className={styles.notificationActions}>
              <button 
                className={styles.acceptButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleVendorRequestAction(notification, true);
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleVendorRequestAction(notification, false);
                }}
              >
                Decline
              </button>
            </div>
          )}
          
          {/* Add action buttons for connection requests */}
          {notification.type === 'CONNECTION_REQUEST' && (!notification.status || notification.status === 'pending') && (
            <div className={styles.notificationActions}>
              <button 
                className={styles.acceptButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleConnectionRequestAction(notification, true);
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleConnectionRequestAction(notification, false);
                }}
              >
                Decline
              </button>
            </div>
          )}

          {/* Show status message when connection request has been processed */}
          {notification.type === 'CONNECTION_REQUEST' && notification.status === 'accepted' && (
            <div className={styles.statusMessage}>
              <span className={styles.acceptedStatus}>You accepted this connection request</span>
            </div>
          )}

          {notification.type === 'CONNECTION_REQUEST' && notification.status === 'rejected' && (
            <div className={styles.statusMessage}>
              <span className={styles.rejectedStatus}>You declined this connection request</span>
            </div>
          )}



{/* Add action buttons for association requests */}
{notification.type === NOTIFICATION_TYPES.ASSOCIATION_REQUEST && (!notification.status || notification.status === 'pending') && (
  <div className={styles.notificationActions}>
    <button 
      className={styles.acceptButton}
      onClick={(e) => {
        e.stopPropagation();
        handleAssociationRequest(notification, true);
      }}
    >
      Accept
    </button>
    <button 
      className={styles.declineButton}
      onClick={(e) => {
        e.stopPropagation();
        handleAssociationRequest(notification, false);
      }}
    >
      Decline
    </button>
  </div>
)}

{/* Show status message when association request has been processed */}
{notification.type === NOTIFICATION_TYPES.ASSOCIATION_REQUEST && notification.status === 'accepted' && (
  <div className={styles.statusMessage}>
    <span className={styles.acceptedStatus}>You accepted this association request</span>
  </div>
)}

{notification.type === NOTIFICATION_TYPES.ASSOCIATION_REQUEST && notification.status === 'rejected' && (
  <div className={styles.statusMessage}>
    <span className={styles.rejectedStatus}>You declined this association request</span>
  </div>
)}






            
          {/* Show status message when vendor request has been processed */}
          {notification.type === 'VENDOR_REQUEST' && notification.status === 'accepted' && (
            <div className={styles.statusMessage}>
              <span className={styles.acceptedStatus}>You accepted this request</span>
            </div>
          )}

          {notification.type === 'VENDOR_REQUEST' && notification.status === 'rejected' && (
            <div className={styles.statusMessage}>
              <span className={styles.rejectedStatus}>You declined this request</span>
            </div>
          )}

          {notification.type === 'STAFF_REQUEST' && (!notification.status || notification.status === 'pending') && (
            <div className={styles.notificationActions}>
              <button 
                className={styles.acceptButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStaffRequest(notification, true);
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStaffRequest(notification, false);
                }}
              >
                Decline
              </button>
            </div>
          )}

          {notification.type === NOTIFICATION_TYPES.ORGARTIST_REQUEST && notification.status === 'accepted' && (
  <div className={styles.statusMessage}>
    <span className={styles.acceptedStatus}>You accepted this artist request</span>
  </div>
)}

{notification.type === NOTIFICATION_TYPES.ORGARTIST_REQUEST && notification.status === 'rejected' && (
  <div className={styles.statusMessage}>
    <span className={styles.rejectedStatus}>You declined this artist request</span>
  </div>
)}
          {/* Show status message when staff request has been processed */}
          {notification.type === 'STAFF_REQUEST' && notification.status === 'accepted' && (
            <div className={styles.statusMessage}>
              <span className={styles.acceptedStatus}>You accepted this request</span>
            </div>
          )}

          {notification.type === 'STAFF_REQUEST' && notification.status === 'rejected' && (
            <div className={styles.statusMessage}>
              <span className={styles.rejectedStatus}>You declined this request</span>
            </div>
          )}
          

          {notification.type === NOTIFICATION_TYPES.ORGMANAGER_REQUEST && (!notification.status || notification.status === 'pending') && (
  <div className={styles.notificationActions}>
    <button 
      className={styles.acceptButton}
      onClick={(e) => {
        e.stopPropagation(); // Prevent opening the modal
        handleManagerRequest(notification, true);
      }}
    >
      Accept
    </button>
    <button 
      className={styles.declineButton}
      onClick={(e) => {
        e.stopPropagation(); // Prevent opening the modal
        handleManagerRequest(notification, false);
      }}
    >
      Decline
    </button>
  </div>
)}

{/* Add action buttons for associated vendor requests */}
{(notification.type === NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REQUEST || 
  notification.type === NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST) && 
  (!notification.status || notification.status === 'pending') && (
  <div className={styles.notificationActions}>
    <button 
      className={styles.acceptButton}
      onClick={(e) => {
        e.stopPropagation();
        handleAssociatedVendorRequest(notification, true);
      }}
    >
      Accept
    </button>
    <button 
      className={styles.declineButton}
      onClick={(e) => {
        e.stopPropagation();
        handleAssociatedVendorRequest(notification, false);
      }}
    >
      Decline
    </button>
  </div>
)}

{/* Show status message when associated vendor request has been accepted */}
{(notification.type === NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REQUEST || 
  notification.type === NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST) && 
  notification.status === 'accepted' && (
  <div className={styles.statusMessage}>
    <span className={styles.acceptedStatus}>You accepted this request</span>
  </div>
)}

{/* Show status message when associated vendor request has been rejected */}
{(notification.type === NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REQUEST || 
  notification.type === NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST) && 
  notification.status === 'rejected' && (
  <div className={styles.statusMessage}>
    <span className={styles.rejectedStatus}>You declined this request</span>
  </div>
)}



{notification.type === NOTIFICATION_TYPES.ORGARTIST_REQUEST && (!notification.status || notification.status === 'pending') && (
  <div className={styles.notificationActions}>
    <button 
      className={styles.acceptButton}
      onClick={(e) => {
        e.stopPropagation(); // Prevent opening the modal
        handleArtistRequest(notification, true);
      }}
    >
      Accept
    </button>
    <button 
      className={styles.declineButton}
      onClick={(e) => {
        e.stopPropagation(); // Prevent opening the modal
        handleArtistRequest(notification, false);
      }}
    >
      Decline
    </button>
  </div>
)}

{/* Add action buttons for vendor staff requests */}
{notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST && (!notification.status || notification.status === 'pending') && (
  <div className={styles.notificationActions}>
    <button 
      className={styles.acceptButton}
      onClick={(e) => {
        e.stopPropagation();
        handleStaffRequest(notification, true); // Using the same handler but will call acceptVendorStaffRequest inside
      }}
    >
      Accept
    </button>
    <button 
      className={styles.declineButton}
      onClick={(e) => {
        e.stopPropagation();
        handleStaffRequest(notification, false); // Using the same handler but will call rejectVendorStaffRequest inside
      }}
    >
      Decline
    </button>
  </div>
)}

{/* Add action buttons for past vendor staff requests */}
{notification.type === NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST && (!notification.status || notification.status === 'pending') && (
  <div className={styles.notificationActions}>
    <button 
      className={styles.acceptButton}
      onClick={(e) => {
        e.stopPropagation();
        handleStaffRequest(notification, true);
      }}
    >
      Accept
    </button>
    <button 
      className={styles.declineButton}
      onClick={(e) => {
        e.stopPropagation();
        handleStaffRequest(notification, false);
      }}
    >
      Decline
    </button>
  </div>
)}


{/* Show status message when vendor staff request has been accepted */}
{notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST && notification.status === 'accepted' && (
  <div className={styles.statusMessage}>
    <span className={styles.acceptedStatus}>You accepted this vendor staff request</span>
  </div>
)}

{/* Show status message when vendor staff request has been rejected */}
{notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_REQUEST && notification.status === 'rejected' && (
  <div className={styles.statusMessage}>
    <span className={styles.rejectedStatus}>You declined this vendor staff request</span>
  </div>
)}

{/* Show status message when past vendor staff request has been accepted */}
{notification.type === NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST && notification.status === 'accepted' && (
  <div className={styles.statusMessage}>
    <span className={styles.acceptedStatus}>You accepted this past vendor staff request</span>
  </div>
)}

{/* Show status message when past vendor staff request has been rejected */}
{notification.type === NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REQUEST && notification.status === 'rejected' && (
  <div className={styles.statusMessage}>
    <span className={styles.rejectedStatus}>You declined this past vendor staff request</span>
  </div>
)}

{/* Show status message when vendor staff permissions have been updated */}
{notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_PERMISSIONS_UPDATED && (
  <div className={styles.statusMessage}>
    <span className={styles.infoStatus}>Your permissions have been updated</span>
  </div>
)}

{/* Show status message when vendor staff has been moved to past */}
{notification.type === NOTIFICATION_TYPES.VENDOR_STAFF_MOVED_TO_PAST && (
  <div className={styles.statusMessage}>
    <span className={styles.infoStatus}>You have been moved to past staff</span>
  </div>
)}



          
          {/* Other notification type displays */}
          {notification.data?._displayData?.tourName && (
            <span className={styles.tourName}>
              {notification.data._displayData.tourName}
            </span>
          )}
          {notification.data?._displayData?.bandName && (
            <span className={styles.bandName}>
              {notification.data._displayData.bandName}
            </span>
          )}
          {notification.data?.actionBy && (
            <div className={styles.actionBy}>
              <img 
                src={notification.data.actionBy.profilePictureUrl || defaultProfilePhoto} 
                alt={notification.data.actionBy.fullName}
                className={styles.actionByPhoto}
              />
              <span>{notification.data.actionBy.fullName}</span>
            </div>
          )}
          
          {/* Add action buttons for invitations */}
          {activeTab === 'invitations' && (!notification.status || notification.status === 'pending') && (
            <div className={styles.notificationActions}>
              <button 
                className={styles.acceptButton}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent click
                  handleEventInvitationAction(notification, true);
                }}
              >
                Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent click
                  handleEventInvitationAction(notification, false);
                }}
              >
                Decline
              </button>
            </div>
          )}

          {/* Show status message when invitation has been processed */}
          {activeTab === 'invitations' && notification.status === 'accepted' && (
            <div className={styles.statusMessage}>
              <span className={styles.acceptedStatus}>You accepted this invitation</span>
            </div>
          )}

          {activeTab === 'invitations' && notification.status === 'rejected' && (
            <div className={styles.statusMessage}>
              <span className={styles.rejectedStatus}>You declined this invitation</span>
            </div>
          )}
        </div>
      )
    };
  }
};


// Function to check for date conflicts in invitations
const checkForInvitationDateConflicts = async (invitation) => {
  try {
    // Extract the assigned dates from the invitation
    const assignedDates = invitation.assignedDates || [];
    if (assignedDates.length === 0) return { hasConflicts: false };
    
    // Fetch the user's assigned dates
    const db = getFirestoreInstance();
    const userAssignedDatesMap = {};
    const assignedDatesRef = collection(db, 'users', userData.userId, 'assignedEventDates');
    const snapshot = await getDocs(assignedDatesRef);
    
    // Build a map of user's assigned dates for easy lookup
    snapshot.forEach(doc => {
      const data = doc.data();
      const dates = data.dates || [];
      
      dates.forEach(dateInfo => {
        // Skip dates from the same event (for re-invitations)
        if (dateInfo.eventId === (invitation.eventId || invitation.tourId)) {
          return;
        }
        
        // Store date info in the map for easy lookup
        userAssignedDatesMap[dateInfo.date] = {
          eventName: dateInfo.eventName || 'Unknown Event',
          venue: dateInfo.venue || {
            displayName: 'Unknown Venue',
            formattedAddress: 'Unknown Location'
          }
        };
      });
    });
    
    // Check each invitation date for conflicts
    const conflicts = [];
    
    for (const dateAssignment of assignedDates) {
      // Handle different date formats in invitations
      const dateStr = dateAssignment.date || 
                      (typeof dateAssignment === 'string' ? dateAssignment : null);
      
      if (!dateStr) continue;
      
      if (userAssignedDatesMap[dateStr]) {
        // Found a conflict
        conflicts.push({
          date: dateStr,
          eventName: userAssignedDatesMap[dateStr].eventName,
          venueDisplayName: userAssignedDatesMap[dateStr].venue.displayName,
          venueAddress: userAssignedDatesMap[dateStr].venue.formattedAddress
        });
      }
    }
    
    return {
      hasConflicts: conflicts.length > 0,
      conflicts
    };
  } catch (error) {
    console.error('Error checking for date conflicts:', error);
    return { hasConflicts: false };
  }
};

// Invitation Conflict Modal component
const InvitationConflictModal = ({ show, details, onClose, onDecline }) => {
  if (!show || !details) return null;
  
  const { conflicts, invitation } = details;
  
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon} />
          Date Conflict
        </h3>
        <p className={styles.modalText}>
          You already have events scheduled on the following dates:
        </p>
        <div className={styles.conflictList}>
          {conflicts.map((conflict, index) => (
            <div key={index} className={styles.conflictItem}>
              <p><strong>Date:</strong> {new Date(conflict.date).toLocaleDateString()}</p>
              <p><strong>Event:</strong> {conflict.eventName}</p>
              <p><strong>Venue:</strong> {conflict.venueDisplayName}</p>
              <p><strong>Location:</strong> {conflict.venueAddress}</p>
            </div>
          ))}
        </div>
        <p className={styles.modalSubText}>
          You cannot accept this invitation due to scheduling conflicts.
        </p>
        <div className={styles.modalActions}>
          <button className={styles.modalCancelButton} onClick={onDecline}>
            Decline Invitation
          </button>
          <button className={styles.modalCloseButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Update the handleEventInvitationAction function to check for conflicts
const handleEventInvitationAction = async (invitation, isAccepted) => {
  try {
    setIsProcessing(true);
    const invitationId = invitation.id || invitation.data?.invitationId;
    if (!invitationId) {
      console.error('No invitation ID found');
      return;
    }
    
    if (isAccepted) {
      // Check for date conflicts before accepting
      const { hasConflicts, conflicts } = await checkForInvitationDateConflicts(invitation);
      
      if (hasConflicts) {
        // Show conflict modal with details
        setInvitationConflictDetails({
          conflicts,
          invitation
        });
        setShowInvitationConflictModal(true);
        setIsProcessing(false);
        return;
      }
      
      // Accept invitation - use the renamed acceptEventInvitation function
      // which properly handles the pendingInvitations subcollection
      await acceptEventInvitation(invitationId, userData.userId);
      
      // Update local state to reflect acceptance
      setNotifications(prev => 
        prev.map(n => 
          n.id === invitation.id 
            ? { ...n, status: 'accepted', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === invitation.id 
          ? { ...n, status: 'accepted', read: true } 
          : n
      );
      
      // Show success message
      alert('Event invitation accepted successfully!');
      
      // Refetch the events list to show the newly added event
      if (fetchEventsData) {
        await fetchEventsData(userData, 1); 
      }
    } else {
      // Decline invitation
      await rejectEventInvitation(invitationId, userData.userId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === invitation.id 
            ? { ...n, status: 'rejected', read: true } 
            : n
        )
      );
      
      // Also update the notifications cache
      allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
        n.id === invitation.id 
          ? { ...n, status: 'rejected', read: true } 
          : n
      );
      
      // Show message
      alert('Event invitation declined.');
    }
    
    // Refresh notifications to get updated status
    fetchNotifications(true);
    
    // Close the modal if it was opened
    if (isModalOpen) {
      setIsModalOpen(false);
    }
  } catch (error) {
    console.error('Error handling event invitation:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} invitation. Please try again.`);
  } finally {
    setIsProcessing(false);
  }
};

// Add handlers for the conflict modal
const handleCloseInvitationConflictModal = () => {
  setShowInvitationConflictModal(false);
  setInvitationConflictDetails(null);
};

const handleDeclineConflictedInvitation = async () => {
  if (!invitationConflictDetails || !invitationConflictDetails.invitation) return;
  
  // Close the modal
  setShowInvitationConflictModal(false);
  
  // Get the invitation
  const invitation = invitationConflictDetails.invitation;
  
  // Use the existing function to decline the invitation
  await handleEventInvitationAction(invitation, false);
  
  // Reset the state
  setInvitationConflictDetails(null);
};


// Add this function to handle band request acceptance/rejection
const handleBandRequestAction = async (notification, isAccepted) => {
  try {
    setIsProcessing(true);
    
    if (!notification.bandInfo || !notification.bandInfo.id) {
      console.error('Missing band information in notification');
      return;
    }
    
    const bandId = notification.bandInfo.id;
    const requestData = notification.data?.requestData || {
      userId: notification.data?.targetUserId,
      userName: notification.data?.targetUserName,
      fullName: notification.data?.targetFullName,
      profilePictureUrl: notification.data?.targetProfilePictureUrl,
      userType: notification.data?.targetUserType,
      userSubType: notification.data?.targetUserSubType
    };
    
    const db = getFirestoreInstance();
    
    if (isAccepted) {
      // Accept the request
      
      // 1. Add user to assignedMembers subcollection with appropriate role
      const memberRole = requestData.userType === 'Manager' ? 'Manager' : 
                        requestData.userType === 'Crew' ? 'Crew' : 'Artist';
      
      const memberData = {
        userId: requestData.userId,
        userName: requestData.userName,
        fullName: requestData.fullName,
        profilePictureUrl: requestData.profilePictureUrl,
        userType: requestData.userType,
        userSubType: requestData.userSubType,
        role: memberRole,
        joinedAt: new Date().toISOString()
      };
      
      // Add to assignedMembers subcollection
      const assignedMemberRef = doc(db, 'bands', bandId, 'assignedMembers', requestData.userId);
      await setDoc(assignedMemberRef, memberData);
      
      // 2. Update the main band document to reflect changes
      // This is for compatibility with existing code
      const bandRef = doc(db, 'bands', bandId);
      const bandDoc = await getDoc(bandRef);
      
      if (bandDoc.exists()) {
        const bandData = bandDoc.data();
        let members = bandData.members || [];
        
        // Add to members array if not already there
        if (!members.some(m => m.userId === requestData.userId)) {
          members.push({
            ...memberData,
            role: memberRole === 'Manager' ? 'admin' : 'member' // For compatibility
          });
          
          await updateDoc(bandRef, { members });
        }
      }
      
      // 3. Add band to user's associatedActs
      const userRef = doc(db, 'users', requestData.userId);
      await updateDoc(userRef, {
        associatedActs: arrayUnion({
          id: bandId,
          type: 'band',
          name: notification.bandInfo.name,
          username: notification.bandInfo.username,
          photoUrl: notification.bandInfo.photoUrl || '',
          role: memberRole,
          joinedAt: new Date().toISOString()
        })
      });
      
      // 4. Send notification to the requester
      await createNotification(requestData.userId, 'general', {
        type: NOTIFICATION_TYPES.BAND_ASSOCIATION_ACCEPTED,
        title: 'Band Association Accepted',
        message: `Your request to be added to ${notification.bandInfo.name} has been accepted.`,
        data: {
          bandId,
          role: memberRole,
          _displayData: {
            bandName: notification.bandInfo.name
          }
        }
      });
      
    } else {
      // Reject the request
      
      // Send rejection notification to requester
      await createNotification(requestData.userId, 'general', {
        type: NOTIFICATION_TYPES.BAND_ASSOCIATION_REJECTED,
        title: 'Band Association Rejected',
        message: `Your request to be added to ${notification.bandInfo.name} has been declined.`,
        data: {
          bandId,
          _displayData: {
            bandName: notification.bandInfo.name
          }
        }
      });
    }
    
    // Mark band notification as read
    await markBandNotificationAsRead(bandId, notification.id, userData.userId);
    
    // Update notifications display
    setBandNotifications(prev => 
      prev.map(n => 
        n.id === notification.id 
          ? { ...n, readBy: { ...(n.readBy || {}), [userData.userId]: new Date() } }
          : n
      )
    );
    
    // Refresh notifications
    await fetchNotifications(true);
    
    alert(`Request ${isAccepted ? 'accepted' : 'declined'} successfully!`);
    
  } catch (error) {
    console.error('Error handling band request:', error);
    alert(`Failed to ${isAccepted ? 'accept' : 'decline'} request. Please try again.`);
  } finally {
    setIsProcessing(false);
  }
};



/**
 * Moves an act from pendingManagingActs to managingActs subcollection
 * Stores only IDs and minimal required data, fetches current data on display
 * Role references removed
 * @param {string} organizationId - ID of the organization
 * @param {string} userId - ID of the artist/user accepting the request
 * @param {string} requestDocId - ID of the request document
 * @returns {Promise<string>} ID of the new managingAct document
 */
const movePendingToManagingAct = async (organizationId, userId, requestDocId) => {
  const db = getFirestoreInstance();
  
  try {
    // 1. Fetch the pending request from pendingManagingActs
    const pendingActRef = doc(db, 'users', organizationId, 'pendingManagingActs', requestDocId);
    const pendingActDoc = await getDoc(pendingActRef);
    
    if (!pendingActDoc.exists()) {
      throw new Error('Pending request not found');
    }
    
    const pendingActData = pendingActDoc.data();
    
    // 2. Create the accepted act entry with minimal data
    // IMPORTANT: Only store IDs and necessary data, not full profiles
    const managingActData = {
      type: pendingActData.type,
      status: 'accepted',
      isPast: pendingActData.isPast || false,
      createdAt: pendingActData.createdAt || new Date().toISOString(),
      acceptedAt: new Date().toISOString()
    };
    
    // Add type-specific ID fields only - no profile data
    if (pendingActData.type === 'artist') {
      managingActData.userId = pendingActData.userId || userId;
    } else {
      managingActData.bandId = pendingActData.bandId || userId;
    }
    
    // Add date information
    if (pendingActData.isPast) {
      managingActData.duration = pendingActData.duration;
    } else {
      managingActData.since = pendingActData.since;
    }
    
    // 3. Add to managingActs subcollection
    const actsRef = collection(db, 'users', organizationId, 'managingActs');
    const newActRef = await addDoc(actsRef, managingActData);
    
    // 4. Update the artist's side of the relationship (only for current, non-past relationships)
    if (pendingActData.type === 'artist' && managingActData.userId && !pendingActData.isPast) {
      try {
        // Get the artist user ID
        const artistUserId = managingActData.userId;
        
        // Create an entry in the artist's managedBy subcollection
        const artistManagedByRef = collection(db, 'users', artistUserId, 'managedBy');
        
        // Add organization to artist's managedBy collection with minimal required data
        await setDoc(doc(artistManagedByRef, organizationId), {
          organizationId: organizationId,
          userId: organizationId, // For consistency with other subcollections
          status: 'accepted',
          since: managingActData.since || { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
          joinedAt: new Date().toISOString(),
          acceptedAt: new Date().toISOString(),
          createdAt: pendingActData.createdAt || new Date().toISOString()
        });
        
        // Update the artist's managedBy array in the main document
        const artistRef = doc(db, 'users', artistUserId);
        const artistDoc = await getDoc(artistRef);
        
        if (artistDoc.exists()) {
          // Get fresh data for the top 3 managers
          const managedByQuery = query(
            artistManagedByRef,
            orderBy('joinedAt', 'desc'),
            limit(3)
          );
          
          const managedBySnapshot = await getDocs(managedByQuery);
          
          // Create enriched top 3 managers array with fresh organization data
          const top3ManagedBy = await Promise.all(
            managedBySnapshot.docs.map(async (docSnapshot) => {
              const managerData = docSnapshot.data();
              const orgId = managerData.organizationId || managerData.userId;
              
              // Get fresh organization data
              const freshOrgDoc = await getDoc(doc(db, 'users', orgId));
              
              if (freshOrgDoc.exists()) {
                const freshOrgData = freshOrgDoc.data();
                return {
                  userId: orgId,
                  userName: freshOrgData.userName || '',
                  fullName: freshOrgData.fullName || '',
                  profilePictureUrl: freshOrgData.profilePictureUrl || '',
                  since: managerData.since || { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
                  addedAt: managerData.joinedAt || new Date().toISOString()
                };
              }
              
              // Fallback if org data not available
              return {
                userId: orgId,
                since: managerData.since || { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
                addedAt: managerData.joinedAt || new Date().toISOString()
              };
            })
          );
          
          // Update artist document with top 3 managers and increment count
          const currentCount = artistDoc.data().managedByCount || 0;
          
          await updateDoc(artistRef, {
            managedBy: top3ManagedBy,
            managedByCount: increment(1)
          });
        }
      } catch (error) {
        console.error('Error updating artist managedBy data:', error);
        // Continue even if this part fails - we don't want to block the entire process
      }
    }
    
    // 5. Remove from pendingManagingActs
    await deleteDoc(pendingActRef);
    
    // 6. Update the main document with top 3 acts
    const top3Query = query(
      actsRef,
      where('isPast', '==', pendingActData.isPast),
      orderBy('createdAt', 'desc'),
      limit(3)
    );
    
    const top3Snapshot = await getDocs(top3Query);
    
    // This array will store enriched acts for UI display
    const top3Acts = await Promise.all(
      top3Snapshot.docs.map(async (docSnapshot) => {
        const actData = docSnapshot.data();
        
        // Enrich with fresh user/band data - ONLY for UI display
        if (actData.type === 'artist' && actData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', actData.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                id: docSnapshot.id,
                ...actData,
                fullName: userData.fullName || '',
                userName: userData.userName || '',
                profilePictureUrl: userData.profilePictureUrl || '',
                userSubType: userData.userSubType || '',
                idVerificationStatus: userData.idVerificationStatus || ''
              };
            }
          } catch (error) {
            console.error(`Error fetching user data for ${actData.userId}:`, error);
          }
        } else if (actData.type === 'band' && actData.bandId) {
          try {
            const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
            if (bandDoc.exists()) {
              const bandData = bandDoc.data();
              return {
                id: docSnapshot.id,
                ...actData,
                fullName: bandData.bandFullName || '',
                userName: bandData.bandUserName || '',
                profilePictureUrl: bandData.bandProfilePictureUrl || '',
                genre: bandData.bandGenres?.join(', ') || ''
              };
            }
          } catch (error) {
            console.error(`Error fetching band data for ${actData.bandId}:`, error);
          }
        }
        
        // Return the act data as is if user/band data enrichment failed
        return {
          id: docSnapshot.id,
          ...actData
        };
      })
    );
    
    // 7. Update main document with top3 and the count
    const userRef = doc(db, 'users', organizationId);
    if (pendingActData.isPast) {
      // For past acts
      await updateDoc(userRef, {
        pastManagingActs: top3Acts,
        pastManagingActsCount: increment(1)
      });
    } else {
      // For current acts
      await updateDoc(userRef, {
        managingActs: top3Acts,
        managingActsCount: increment(1)
      });
    }
    
    return newActRef.id;
  } catch (error) {
    console.error('Error moving act from pending to managed:', error);
    throw error;
  }
};

// Mark all as read
const handleMarkAllAsRead = async () => {
if (activeTab === 'bands') {
// Mark all band notifications as read
if (bandNotifications.length === 0) return;
  try {
    // Mark each unread notification as read
    await Promise.all(
      bandNotifications
        .filter(notification => !isBandNotificationRead(notification, userData.userId))
        .map(notification => 
          markBandNotificationAsRead(notification.bandInfo.id, notification.id, userData.userId)
        )
    );
    
    // Update band notifications in the component
    setBandNotifications(prev => 
      prev.map(n => ({
        ...n,
        readBy: { ...(n.readBy || {}), [userData.userId]: new Date() }
      }))
    );
    
    // Update all notifications cache
    allNotificationsRef.current.bands = allNotificationsRef.current.bands.map(n => ({
      ...n,
      readBy: { ...(n.readBy || {}), [userData.userId]: new Date() }
    }));
    
    // Update unread count
    setUnreadCount(prev => ({
      ...prev,
      bands: 0
    }));
  } catch (error) {
    console.error('Error marking all band notifications as read:', error);
  }
} else {
  // Mark all general/invitation notifications as read
  if (notifications.length === 0) return;
  
  const unreadIds = notifications
    .filter(notification => !notification.read)
    .map(notification => notification.id);
  
  if (unreadIds.length === 0) return;
  
  try {
    await markAsRead(userData.userId, unreadIds, activeTab);
    
    // Update notifications in the component
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
    
    // Update all notifications cache
    allNotificationsRef.current[activeTab] = allNotificationsRef.current[activeTab].map(n => 
      ({ ...n, read: true })
    );
    
    // Update unread count
    setUnreadCount(prev => ({
      ...prev,
      [activeTab]: 0
    }));
  } catch (error) {
    console.error('Error marking all as read:', error);
  }
}
};
const handleRefresh = () => {
fetchNotifications(true);
};
// Show loading spinner
if (loading) {
return (
<div className={styles.panel}>
<div className={styles.loadingSpinner} />
</div>
);
}
return (
<div className={styles.panel}>
{isMobile && (
<button 
       className={styles.mobileCloseButton}
       onClick={onClose}
       aria-label="Close notifications"
     >
<FontAwesomeIcon icon={faTimes} />
</button>
)}
  <div className={styles.header}>
    <h2>Notifications</h2>
    <div className={styles.headerActions}>
      {((activeTab === 'general' && unreadCount.general > 0) || 
        (activeTab === 'invitations' && unreadCount.invitations > 0) ||
        (activeTab === 'bands' && unreadCount.bands > 0)) && (
        <button 
          className={styles.markAllReadButton}
          onClick={handleMarkAllAsRead}
        >
          Mark all as read
        </button>
      )}
      <button 
        className={styles.refreshButton}
        onClick={handleRefresh}
      >
        <FontAwesomeIcon icon={faCheck} />
      </button>
    </div>
  </div>

  <div className={styles.tabs}>
    <button 
      className={`${styles.tab} ${activeTab === 'general' ? styles.active : ''}`}
      onClick={() => setActiveTab('general')}
    >
      General
      {unreadCount.general > 0 && (
        <span className={styles.unreadBadge}>{unreadCount.general}</span>
      )}
    </button>
    <button 
      className={`${styles.tab} ${activeTab === 'bands' ? styles.active : ''}`}
      onClick={() => setActiveTab('bands')}
    >
      Bands
      {unreadCount.bands > 0 && (
        <span className={styles.unreadBadge}>{unreadCount.bands}</span>
      )}
    </button>
    <button 
      className={`${styles.tab} ${activeTab === 'invitations' ? styles.active : ''}`}
      onClick={() => setActiveTab('invitations')}
    >
      Invitations
      {unreadCount.invitations > 0 && (
        <span className={styles.unreadBadge}>{unreadCount.invitations}</span>
      )}
    </button>
  </div>

  <div className={styles.notificationsList}>
    {error ? (
      <div className={styles.errorState}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button 
          className={styles.retryButton}
          onClick={() => fetchNotifications(true)}
        >
          Retry
        </button>
      </div>
    ) : activeTab === 'bands' ? (
      // Band notifications
      bandNotifications.length === 0 ? (
        <div className={styles.emptyState}>
          <FontAwesomeIcon icon={faMusic} className={styles.emptyIcon} />
          <p>No band notifications yet</p>
          {userBandsRef.current.length === 0 && (
            <p className={styles.subMessage}>Join or create a band to get notifications</p>
          )}
        </div>
      ) : (
        <>
          {bandNotifications.map(notification => {
            const { icon, content } = getNotificationContent(notification);
            const isRead = isBandNotificationRead(notification, userData.userId);
            
            return (
              <div
                key={notification.id}
                className={`${styles.notificationItem} ${isRead ? styles.read : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className={`${styles.notificationIcon} ${styles[notification.type.toLowerCase()]}`}>
                  <FontAwesomeIcon icon={icon} />
                </div>
                {content}
              </div>
            );
          })}
          
          {hasMore && (
            <button
              className={styles.loadMoreButton}
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                'Show More'
              )}
            </button>
          )}
        </>
      )
    ) : notifications.length === 0 ? (
      // Empty state for general/invitations
      <div className={styles.emptyState}>
        <FontAwesomeIcon icon={faBell} className={styles.emptyIcon} />
        <p>No {activeTab === 'general' ? 'notifications' : 'invitations'} yet</p>
      </div>
    ) : (
      // General/invitation notifications
      <>
        {notifications.map(notification => {
          const { icon, content } = getNotificationContent(notification);
          
          return (
            <div
              key={notification.id}
              className={`${styles.notificationItem} ${notification.read ? styles.read : ''}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className={`${styles.notificationIcon} ${styles[notification.type.toLowerCase()]}`}>
                <FontAwesomeIcon icon={icon} />
              </div>
              {content}
            </div>
          );
        })}
        
        {hasMore && (
          <button
            className={styles.loadMoreButton}
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              'Show More'
            )}
          </button>
        )}
      </>
    )}
    
  </div>

  {/* Invitation Conflict Modal */}
<InvitationConflictModal
  show={showInvitationConflictModal}
  details={invitationConflictDetails}
  onClose={handleCloseInvitationConflictModal}
  onDecline={handleDeclineConflictedInvitation}
/>
  
  {/* Details Modal */}
  {isModalOpen && selectedNotification && (
    <NotificationDetailsModal 
  notification={selectedNotification}
  onClose={() => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  }}
  handleEventInvitationAction={handleEventInvitationAction}
  handleStaffRequest={handleStaffRequest}
  handleVendorRequestAction={handleVendorRequestAction}
  handleConnectionRequestAction={handleConnectionRequestAction}
  handleManagerRequest={handleManagerRequest}
  handleArtistRequest={handleArtistRequest}
  handleAssociatedVendorRequest={handleAssociatedVendorRequest}
/>
  )}
  
  {isProcessing && (
    <div className={styles.processingOverlay}>
      <div className={styles.processingSpinner}></div>
      <p>Processing request...</p>
    </div>
  )}
</div>
);
};
export default Notifications;